import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Divider,
  Image,
  message,
  Spin,
  Tag,
  Tooltip,
  Modal,
  Select,
} from "antd";
import {
  CreateQuotationStepsEnum,
  QuoteStatus,
  WarningSeverity,
  PaymentMilestoneStatus,
  PaymentMilestoneStatusLabels,
  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT,
  isMezzanineCategory,
} from "../quotationTypes";
import {
  getAdditionalInfoFieldsAPI,
  getQuotationDetailsAPI,
  updateQuotatioStatusAPI,
  previewQuoteEmailAPI,
  openSystemMailer,
  getLatestExchangeRateAPI,
  updateQuotationAPI,
  updateMarginDiscountAPI,
  getQuoteReverseCalculationAPI,
  ReverseCalculationData,
} from "../quotationApis";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
  ATTRIBUTE_ORDER,
  capitalizeFirstLetter,
  CapitalizeFirstWord,
  DecriptEnv,
  formatAddress,
  formatDeliveryAddress,
  formatCurrency,
  formatToLocalDate,
  getFullAwsUrl,
  getStaticSalespersonLabel,
} from "../../../../helpers/utils";
import { EyeOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import placeHolderImage from "./../../../../Assets/Images/placeholderImage.png";
import { svg } from "../../../../Assets/svg";
import { PriceTypeLabels, ServicePriceType } from "../../Services/serviceTypes";
import {
  getMajorProductType,
  MajorProductTypes,
  ProductDropEnum,
  ProductDropLabels,
  ProductFinishEnum,
  ProductFinishLabels,
} from "../../Products/productsTypes";
import {
  decryptByText,
  getDecryptedName,
  getDecryptedUserData,
  getUserRole,
  isAdminSubAdminLoggedIn,
  isCustomerLoggedIn,
} from "../../../../helpers/bycrypt";
import { ALLOWED_ROLES_FOR_EXCHANGE_RATE } from "../../../../helpers/permissionModule";

// ✅ REDUX IMPORTS
import {
  resetQuotationState,
  selectQuotationDetails,
  selectShowRetailPrice,
  setQuotationDetails,
} from "../quotationSlice";
import {
  WarningsModal,
  WarningActionLayouts,
} from "../../../../Components/WarningsModal";
import NoItemsAddedModal from "./NoItemsAddedModal";
import { CustomExchangeRateModal } from "./CustomExchangeRateModal";
import { MarginDiscountModal } from "./MarginDiscountModal";
import { AdditionalInfoField, AdditionalInfoValue } from "../quotationTypes";
import {
  CategorySlugs,
  SummaryBlockLabel,
  SummaryFieldsSchema,
} from "./Products/SummaryHelper";
import { APRView } from "./Products/Forms/APRForm";
import {
  DeliveryLocationQtyType,
  DeliveryLocationQtyTypeColors,
  DeliveryLocationType,
  getDeliveryPriceLabel,
  getDeliveryQtyTypeLabel,
} from "../../MasterData/DeliveryLocations";
import { RoleTypesEnum } from "../../RolesAndPermissions/rolesAndPermissionsTypes";
import {
  getVatValue,
  VatType,
} from "../../ClientsDistributors/clientDistributorTypes";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";
import { AttachedDrawings } from "../QuotationDetails/ProductDetails";
import EstimationFloorDetails from "../../Estimations/steps/FloorDetails";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import { DownloadMailerAppModal } from "../../../../Components/DownloadMailerAppModal";
import {
  DesktopUpdateModal,
  isVersionNewer,
} from "../../../../Components/DesktopUpdateModal";
import { io } from "socket.io-client";
import { useSocket } from "../../../../contexts/SocketContext";
import { Capacitor } from "@capacitor/core";
import { openQuoteEmailWithPdf } from "../../../../helpers/emailCapacitor";
import DescriptionWithShowMore from "../../../../helpers/DescriptionWithShowMore";
import ChangeEstimatedLeadTimeModal from "./ChangeEstimatedLeadTimeModal";

// Asset Base URL
const IMG_URL_BASE = `${DecriptEnv(
  process.env.REACT_APP_AWS_ENDPOINT,
)}static-images/`;

// --- Configuration Mapping ---

const getCleanFileName = (url: string) => {
  try {
    const parts = url.split("/");
    const fullName = parts[parts.length - 1];
    if (fullName.includes("_") && fullName.length > 37) {
      return fullName.substring(fullName.indexOf("_") + 1);
    }
    return fullName;
  } catch (e) {
    return "File";
  }
};

interface ReviewProps {
  readOnly?: boolean;
  quoteId?: string;
}

export default function Review({
  readOnly = false,
  quoteId: quoteIdProp,
}: ReviewProps = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const quotationId = quoteIdProp || searchParams.get("quotationId");
  const mode = searchParams.get("mode");

  const [isExchangeManual, setIsExchangeManual] = useState(false);
  const [exchangeDecimal, setExchangeDecimal] = useState(2);
  const [showCustomRateModal, setShowCustomRateModal] = useState(false);
  const [customRateInput, setCustomRateInput] = useState<number | "">("");
  const [isMarginDiscountModalOpen, setIsMarginDiscountModalOpen] =
    useState(false);

  const [isDiscountChangeLoading, setIsDiscountChangeLoading] = useState(false);

  const handleApplyMarginDiscount = (margin: number, discount: number) => {
    const quoteId = quotationId || quotationDetails?._id;
    if (!quoteId) {
      dispatch(
        setQuotationDetails({
          ...quotationDetails,
          margin: margin > 0 ? margin : 0,
          customer_snapshot: {
            ...quotationDetails?.customer_snapshot,
            margin: margin > 0 ? margin : 0,
            discount: discount > 0 ? discount : 0,
          },
        }),
      );
      setIsMarginDiscountModalOpen(false);
      return;
    }
    setIsDiscountChangeLoading(true);
    dispatch(
      updateMarginDiscountAPI({
        payload: {
          quote_id: quoteId,
          margin: margin > 0 ? margin : 0,
          discount: discount > 0 ? discount : 0,
        },
        onSuccess: (data: any) => {
          if (data) {
            dispatch(setQuotationDetails(data));
          } else {
            dispatch(
              getQuotationDetailsAPI({
                quote_id: quoteId,
                onSuccess: (response: any) => {
                  dispatch(setQuotationDetails(response));
                },
              }),
            );
          }
          setIsDiscountChangeLoading(false);
          setIsMarginDiscountModalOpen(false);
        },
        onError: () => {
          setIsDiscountChangeLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    dispatch(
      getLatestExchangeRateAPI({
        onSuccess: (data: any) => {
          setIsExchangeManual(!!data?.is_manual);
          if (typeof data?.decimal === "number") {
            setExchangeDecimal(data?.decimal);
          }
        },
        onError: () => {},
      }),
    );
  }, [dispatch]);

  const { socket, isConnected }: any = useSocket();

  // ✅ 1. SINGLE SOURCE OF TRUTH
  const quotationDetails = useAppSelector(selectQuotationDetails);
  const showRetailPriceKey = useAppSelector(selectShowRetailPrice);
  const isRetailPriceVisible = !(isCustomerLoggedIn() && !showRetailPriceKey);
  const showHiddenRetailPriceTooltip =
    !isCustomerLoggedIn() && !showRetailPriceKey;
  // Local loading state for the background refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  // const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const [isNoItemsModalOpen, setIsNoItemsModalOpen] = useState(false);
  const [apiWarnings, setApiWarnings] = useState<any[]>([]);
  const [apiWarningSeverity, setApiWarningSeverity] = useState<
    WarningSeverity | ""
  >("");
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showEmailSentConfirmModal, setShowEmailSentConfirmModal] =
    useState(false);
  const [showDesktopAppError, setShowDesktopAppError] = useState(false);
  const [showDesktopUpdateModal, setShowDesktopUpdateModal] =
    useState<boolean>(false);
  const [desktopAppCurrentVersion, setDesktopAppCurrentVersion] =
    useState<string>("1.0.0");
  const pendingSendMailRef = useRef<(() => void) | null>(null);
  const [pendingSentForceUpdate, setPendingSentForceUpdate] = useState(false);
  const [isContantLoading, setIsContantLoading] = useState<boolean>(true);
  const mailerTimeoutRef = useRef<React.MutableRefObject<any> | any>(null);

  const [showLeadTimeModal, setShowLeadTimeModal] = useState(false);
  const currentLeadTimeDays = quotationDetails?.estimated_lead_time;
  const currentLeadTimeWeeks =
    currentLeadTimeDays != null
      ? Number((currentLeadTimeDays / 7).toFixed(1))
      : null;

  const [reverseCalcData, setReverseCalcData] =
    useState<ReverseCalculationData | null>(null);

  const activeQuoteId = quotationId || quotationDetails?._id;

  const fetchReverseCalculation = (targetId?: string) => {
    const idToFetch = targetId || activeQuoteId;
    if (!idToFetch) return;

    dispatch(
      getQuoteReverseCalculationAPI({
        quote_id: idToFetch,
        onSuccess: (data) => {
          setReverseCalcData(data);
        },
        onError: () => {
          setReverseCalcData(null);
        },
      }),
    );
  };

  useEffect(() => {
    if (activeQuoteId) {
      fetchReverseCalculation(activeQuoteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuoteId, quotationDetails?.grand_total, dispatch]);

  const isReverseCalculationUnbalanced = Boolean(
    reverseCalcData &&
      reverseCalcData.is_balanced === false &&
      typeof reverseCalcData.remaining_balance === "number" &&
      (reverseCalcData.remaining_balance > 1 ||
        reverseCalcData.remaining_balance < -1),
  );

  const remainingDiscrepancyBalance = reverseCalcData?.remaining_balance || 0;
  const formattedDiscrepancyAmount = formatCurrency(
    remainingDiscrepancyBalance,
    quotationDetails?.currency || reverseCalcData?.currency,
    quotationDetails?.conversion_rate,
  );

  useEffect(() => {
    if (!socket) return;

    const handleMailerLoader = (data: any) => {
      console.log("📥 Received mailer:trigger-loader event in Review:", data);
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
        mailerTimeoutRef.current = null;
      }
      if (
        data &&
        (data.loader === false ||
          data.loading === false ||
          data.loder === false ||
          data.status === "success" ||
          data.status === "error")
      ) {
        setIsContantLoading(false);
      } else if (
        data &&
        (data.loader === true ||
          data.loading === true ||
          data.loder === true ||
          data.status === "processing")
      ) {
        setIsContantLoading(true);
      }
    };

    socket.on("mailer:trigger-loader", handleMailerLoader);

    return () => {
      socket.off("mailer:trigger-loader", handleMailerLoader);
      if (mailerTimeoutRef.current) {
        clearTimeout(mailerTimeoutRef.current);
      }
    };
  }, [socket]);

  const triggerEmailWithDesktopAppCheck = (sendEmailCallback: () => void) => {
    if (!socket || !isConnected) {
      console.error("Global socket is not connected!");
      message.destroy("opening_mailer_key");
      setShowDesktopAppError(true);
      return;
    }

    const targetRoom = localStorage.getItem("sm_target_room");
    const requiredVersion =
      process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1";

    let responseReceived = false;
    let checkVersionTimeout: NodeJS.Timeout | null = null;

    const handleCheckVersionResponse = (data: any) => {
      if (responseReceived) return;
      responseReceived = true;

      if (checkVersionTimeout) {
        clearTimeout(checkVersionTimeout);
        checkVersionTimeout = null;
      }
      if (socket) {
        socket.off("check_version_response", handleCheckVersionResponse);
      }

      console.log(
        "📥 Received check_version_response payload in Review:",
        data,
      );
      const currentVersion =
        data?.current_version || data?.app_version || data?.version || "1.0.0";
      setDesktopAppCurrentVersion(currentVersion);

      const isOutdated = isVersionNewer(requiredVersion, currentVersion);
      const isMatched =
        !isOutdated &&
        (data?.is_matched !== undefined ? Boolean(data.is_matched) : true);

      if (!isMatched) {
        console.warn(
          `⚠️ Desktop app version mismatch. Required: ${requiredVersion}, Installed: ${currentVersion}`,
        );
        message.destroy("opening_mailer_key");
        pendingSendMailRef.current = () => {
          sendEmailCallback();
        };
        setShowDesktopUpdateModal(true);
      } else {
        console.log("✅ Desktop app is running and version matches!");
        sendEmailCallback();
      }
    };

    checkVersionTimeout = setTimeout(() => {
      if (!responseReceived) {
        responseReceived = true;
        if (socket) {
          socket.off("check_version_response", handleCheckVersionResponse);
        }
        console.warn(
          "⚠️ Desktop Mailer App did not respond to check_version within timeout.",
        );
        message.destroy("opening_mailer_key");

        if (targetRoom) {
          console.warn(
            "⚠️ Legacy desktop app detected in Review. Prompting update modal.",
          );
          setDesktopAppCurrentVersion("1.0.0 (Legacy)");
          pendingSendMailRef.current = () => {
            sendEmailCallback();
          };
          setShowDesktopUpdateModal(true);
        } else {
          setShowDesktopAppError(true);
        }
      }
    }, 2500);

    socket.on("check_version_response", handleCheckVersionResponse);

    console.log(
      "📤 Emitting check_version to socket for target room:",
      targetRoom || "all",
    );
    socket.emit("check_version", {
      target_room: targetRoom || undefined,
      required_version: requiredVersion,
      version: requiredVersion,
    });
  };

  const [additionalInfoFields, setAdditionalInfoFields] = useState<
    AdditionalInfoField[]
  >([]);
  const [loadingFields, setLoadingFields] = useState(false);

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  // --- 2. ALWAYS FETCH LOGIC ---
  useEffect(() => {
    if (!quotationId) return;

    // Show spinner ONLY if we have absolutely no data to show yet
    const hasData =
      quotationDetails &&
      (quotationDetails._id === quotationId ||
        quotationDetails.quote_id === quotationId);

    if (!hasData) {
      setIsRefreshing(true);
    } else {
      console.log("⚡ Showing cached data while refreshing in background...");
    }

    // ✅ ALWAYS Fetch to ensure Review is 100% accurate (Calculations, VAT, etc.)
    dispatch(
      getQuotationDetailsAPI({
        quote_id: quotationId,
        onSuccess: (response: any) => {
          dispatch(setQuotationDetails(response));
          setIsRefreshing(false);

          if (response?._id) {
            fetchReverseCalculation(response._id);
          }

          // After getting details, we have the category ID to fetch metadata
          if (response?.categories_snapshot?.category_id) {
            setLoadingFields(true);
            dispatch(
              getAdditionalInfoFieldsAPI({
                category: response.categories_snapshot.category_id,
                onSuccess: (res) => {
                  const fields = res?.data || [];
                  setAdditionalInfoFields(fields);
                  setLoadingFields(false);
                },
                onError: () => setLoadingFields(false),
              }),
            );
          }
        },
        onError: () => {
          setIsRefreshing(false);
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId, dispatch]);

  // --- Navigation Helper ---
  const goToStep = (step: CreateQuotationStepsEnum) => {
    const params = new URLSearchParams(searchParams);
    params.set("step", step);
    navigate({ search: params.toString() });
  };

  const isDiscountAllow = (): boolean => {
    const userData = getDecryptedUserData();

    if (!userData || !userData.role) {
      return false;
    }

    return (
      userData.role.key === RoleTypesEnum.ADMIN ||
      userData.role.key === RoleTypesEnum.SUB_ADMIN ||
      userData.role.key === RoleTypesEnum.CREDIT_CONTROLLER
    );
  };

  // const renderDynamicInfoItem = (field: AdditionalInfoField, slug: any) => {
  //   let option: AdditionalInfoValue | undefined;
  //   if (field.type === "toggle_with_boolean") {
  //     option = field.value.find(
  //       (v) => v.slug === (slug === true ? "yes" : "no"),
  //     );
  //   } else {
  //     option = field.value.find((v) => v.slug === slug);
  //   }

  //   if (!option)
  //     return <span className="fw-semibold text-dark fs-6">{String(slug)}</span>;

  //   const showDate =
  //     option.slug === "installation_required" ||
  //     option.slug === "installation-work"
  //       ? additionalInfo.installation_date
  //       : null;

  //   return (
  //     <div className="d-flex align-items-center gap-2 mb-1">
  //       {option.image && (
  //         <img
  //           src={getFullAwsUrl(option.image, true)}
  //           width={30}
  //           height={30}
  //           className="object-fit-cover rounded-1 border"
  //           alt={option.name}
  //         />
  //       )}
  //       <div>
  //         <span className="fw-semibold text-dark fs-6 lh-m">{option.name}</span>
  //         {showDate && (
  //           <span
  //             className="d-block fs-8 text-949494"
  //             style={{ fontSize: "12px" }}
  //           >
  //             Date: {formatToLocalDate(showDate)}
  //           </span>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  // Only block UI if we have absolutely no data.
  // Otherwise, let the user see the stale data while it updates (Optimistic UI).
  if (isRefreshing && !quotationDetails) {
    return (
      <div className="text-center py-5">
        <Spin size="large" tip="Loading review..." />
      </div>
    );
  }

  if (!quotationDetails) return null;

  // ✅ USE REDUX DATA
  const data = quotationDetails;
  const isInstallationRequired = data?.installation_mark_up_required;
  const snapshot = data.customer_snapshot || {};
  const milestones = data.payment_milestones || [];
  const preferences = snapshot.preferences || {};
  const serviceList = data.services || [];
  const designList = data.designs || [];
  const checklist = data.checklist_answers || [];
  const internalNotes = data.notes?.internal || [];
  const clientNotes = data.notes?.client || [];

  const deliveryDetails = data?.delivery;
  const isCourierDelivery =
    deliveryDetails?.qty_type === "parcel_box" ||
    deliveryDetails?.qty_type === DeliveryLocationQtyType.PARCEL_BOX ||
    deliveryDetails?.method === "parcel_box" ||
    (typeof deliveryDetails?.name === "string" &&
      deliveryDetails.name.toLowerCase().includes("courier delivery")) ||
    (Array.isArray(deliveryDetails?.boxes) &&
      deliveryDetails.boxes.length > 0) ||
    (Array.isArray(deliveryDetails?.parcel_boxes) &&
      deliveryDetails.parcel_boxes.length > 0);

  const parcelBoxes =
    deliveryDetails?.boxes || deliveryDetails?.parcel_boxes || [];

  const machineDeliveryDetails = data?.machine_service_delivery || [];
  const mezzanineDeliveryDetails = data?.mezzanine_delivery;

  const isAdminApprovalOn = quotationDetails?.admin_approval_required ?? false;

  const getFilledByText = (item: any) => {
    if (!item.updated_by) return "";
    const name = getDecryptedName(item.updated_by);
    const date = formatToLocalDate(item.updated_at, true);
    return `Filled By: ${name} on ${date}`;
  };

  const renderChecklistAnswer = (item: any) => {
    const type = item.ans_type;
    const answer = item.answer;
    const conditionalVal = item.conditional?.value;
    const isValid = (val: any) =>
      val !== null && val !== undefined && val !== "";

    const renderFiles = (urls: string[]) => {
      if (!Array.isArray(urls) || urls.length === 0) return null;
      return (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {urls.map((url: string, idx: number) => (
            <Tag
              key={idx}
              className="d-flex align-items-center gap-2 px-3 py-1 bg-white border rounded-2 fs-6 fw-bold cursor-pointer"
              style={{
                border: "1px solid #E0E0E0",
                whiteSpace: "normal",
                wordBreak: "break-all",
                maxWidth: "100%",
                height: "auto",
              }}
              onClick={() => {
                setPreviewFiles(urls);
                setPreviewIndex(idx);
                setPreviewModalOpen(true);
              }}
            >
              {getCleanFileName(url)}
            </Tag>
          ))}
        </div>
      );
    };

    switch (type) {
      case "TEXT":
      case "LONG_TEXT":
        return (
          <span className="fw-bold fs-6 text-dark">
            {isValid(answer) ? answer : "-"}
          </span>
        );
      case "YES_NO":
        let baseLabel = "-";
        if (answer === true) baseLabel = "Yes";
        if (answer === false) baseLabel = "No";

        if (
          typeof conditionalVal === "string" &&
          conditionalVal.trim() !== ""
        ) {
          return (
            <div>
              <span className="fw-bold fs-6 text-dark">{baseLabel}, </span>
              <span className="fw-bold fs-6 text-dark">{conditionalVal}</span>
            </div>
          );
        }
        if (Array.isArray(conditionalVal) && conditionalVal.length > 0) {
          return (
            <div>
              <span className="fw-bold fs-6 text-dark">{baseLabel}</span>
              {renderFiles(conditionalVal)}
            </div>
          );
        }
        return <span className="fw-bold fs-6 text-dark">{baseLabel}</span>;
      case "FILE_UPLOAD":
        if (Array.isArray(answer) && answer.length > 0) {
          return renderFiles(answer);
        }
        return <span className="fw-bold fs-6 text-dark">-</span>;
      default:
        return <span className="fw-bold fs-6 text-dark">-</span>;
    }
  };

  const formatAttributes = (attrs: any) => {
    if (!attrs) return "";
    const parts = ATTRIBUTE_ORDER.filter((def) => attrs[def.key]).map((def) => {
      let val = attrs[def.key];
      const label = def.label.replace(/\s*\(.*\)/, "");
      let displayVal = val;

      if (def.key === "finish" || def.key === "secondary_finish") {
        displayVal = ProductFinishLabels[val as ProductFinishEnum] || val;
      } else if (def.key === "drop") {
        displayVal = ProductDropLabels[val as ProductDropEnum] || val;
      } else if (def.key === "custom_drop") {
        displayVal = `${val}mm`;
      } else if (typeof val === "string") {
        displayVal = val.split("_").map(capitalizeFirstLetter).join(" ");
      }

      return `${label}: ${displayVal}`;
    });

    const nonStandardColour =
      attrs.non_standard_product_colour ||
      attrs.non_standard_accessories_colour ||
      attrs.non_standard_replacement_parts_colour;
    const nonStandardPaintCode =
      attrs.non_standard_product_paint_code ||
      attrs.non_standard_accessories_paint_code ||
      attrs.non_standard_replacement_parts_paint_code;

    if (nonStandardColour) {
      parts.push(`Colour: ${nonStandardColour}`);
    }
    if (nonStandardPaintCode) {
      parts.push(`Paint Code: ${nonStandardPaintCode}`);
    }

    const nonStandardSecondaryColour =
      attrs.non_standard_secondary_product_colour;
    const nonStandardSecondaryPaintCode =
      attrs.non_standard_secondary_product_paint_code;

    if (nonStandardSecondaryColour) {
      parts.push(`Secondary Colour: ${nonStandardSecondaryColour}`);
    }
    if (nonStandardSecondaryPaintCode) {
      parts.push(`Secondary Paint Code: ${nonStandardSecondaryPaintCode}`);
    }

    return parts.join(" | ");
  };

  const renderNoteItem = (note: any) => {
    const rawAttachments = note?.attachments || [];
    const attachmentUrls: string[] = rawAttachments
      .map((a: any) => (typeof a === "string" ? a : a?.file_url || ""))
      .filter(Boolean);

    return (
      <div
        key={note._id}
        className="card border-light-subtle rounded-3 mb-2 shadow-none"
      >
        <div className="card-body py-2 px-md-3 px-2">
          <div className="d-flex justify-content-between gap-3">
            <p className="mb-0 fs-6 fw-semibold text-primary d-flex align-items-center text-break">
              {note.content}
            </p>
          </div>
          <div className="d-flex flex-wrap column-gap-3 fs-7 mt-1">
            <small className="text-949494">
              {note.created_by
                ? `Created By: ${getDecryptedName(
                    note.created_by,
                  )} on ${formatToLocalDate(note.created_at, true)}`
                : ""}
            </small>
            {note.updated_by && note.updated_at && (
              <small className="text-949494">
                Last Modified By: {getDecryptedName(note.updated_by)} on{" "}
                {formatToLocalDate(note.updated_at, true)}
              </small>
            )}
          </div>

          {attachmentUrls.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {attachmentUrls.map((url: string, idx: number) => (
                <Tag
                  key={idx}
                  className="d-flex align-items-center gap-2 px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer"
                  style={{
                    border: "1px solid #E0E0E0",
                    whiteSpace: "normal",
                    wordBreak: "break-all",
                    maxWidth: "100%",
                    height: "auto",
                  }}
                  onClick={() => {
                    setPreviewFiles(attachmentUrls);
                    setPreviewIndex(idx);
                    setPreviewModalOpen(true);
                  }}
                >
                  <span title="Click to view file">
                    {getCleanFileName(url)}
                  </span>
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const executeStatusUpdate = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "pending_admin_approval"
      | "admin_approved",
    forceUpdate: boolean = false,
  ) => {
    const payload = {
      quote_id: quotationId as string,
      status,
      force_update: forceUpdate,
    };
    setLoadingAction(status);
    dispatch(
      updateQuotatioStatusAPI({
        payload,
        onSuccess: () => {
          dispatch(resetQuotationState());
          navigate("/quotations");
          setLoadingAction(null);
          setApiWarnings([]);
        },
        onWarnings: (warnings, severity) => {
          setApiWarnings(warnings);
          setApiWarningSeverity(severity || "");
          setPendingStatus(status);
          setLoadingAction(null);
        },
        onError: () => {
          setLoadingAction(null);
        },
        onNoItemsError: () => {
          setIsNoItemsModalOpen(true);
        },
      }),
    );
  };

  const changeStatusHandler = (
    status:
      | "saved"
      | "sent"
      | "design_pending"
      | "design_approved"
      | "pending_admin_approval"
      | "admin_approved",
    forceUpdate: boolean = false,
  ) => {
    const areNoProductsRequired =
      quotationDetails?.no_products_required ?? false;

    const hasProducts =
      Array.isArray(quotationDetails?.products) &&
      quotationDetails.products.some(
        (group: any) =>
          group.products?.length > 0 ||
          group.accessories?.length > 0 ||
          group.replacement_parts?.length > 0,
      );

    const hasServices =
      Array.isArray(quotationDetails?.services) &&
      quotationDetails.services.some((s: any) => Number(s.line_total) > 0);

    const hasMezzanine =
      Boolean(quotationDetails?.mezzanine_floor) ||
      (Array.isArray(quotationDetails?.categories_snapshot) &&
        quotationDetails.categories_snapshot.some((cat: any) =>
          isMezzanineCategory(cat),
        ));

    const hasBespokeItems =
      Array.isArray(quotationDetails?.bespoke_items) &&
      quotationDetails.bespoke_items.length > 0;

    const hasValidProducts = !areNoProductsRequired && hasProducts;

    const hasAnyItems =
      hasValidProducts || hasServices || hasMezzanine || hasBespokeItems;

    if (!hasAnyItems && !forceUpdate) {
      setIsNoItemsModalOpen(true);
      return;
    }

    if (status === "sent") {
      setLoadingAction("sent");
      message.loading({
        content: "Preparing email format and opening mail app...",
        key: "opening_mailer_key",
        duration: 0,
      });
      dispatch(
        previewQuoteEmailAPI({
          payload: { quote_id: quotationId as string },
          onSuccess: async (emailData) => {
            setLoadingAction(null);
            message.destroy("opening_mailer_key");
            try {
              if (Capacitor.isNativePlatform()) {
                message.loading({
                  content: "Preparing quotation PDF and opening mail app...",
                  key: "opening_mailer_key",
                  duration: 0,
                });
                try {
                  await openQuoteEmailWithPdf(quotationId as string, emailData);
                  message.destroy("opening_mailer_key");
                  setPendingSentForceUpdate(forceUpdate);
                  setIsContantLoading(false);
                  setShowEmailSentConfirmModal(true);
                } catch (error) {
                  console.error("Native email composer error:", error);
                  message.destroy("opening_mailer_key");
                  message.error(
                    "Failed to prepare the quotation email. Please try again.",
                  );
                }
                return;
              }
              triggerEmailWithDesktopAppCheck(() => {
                const targetRoom = localStorage.getItem("sm_target_room");
                console.log(
                  "Emitting email data to global socket for target room:",
                  targetRoom || "all",
                );
                socket.emit("mailer:trigger-email", {
                  target_room: targetRoom || undefined,
                  to: emailData.to || "",
                  cc: emailData.cc || "",
                  subject: emailData.mail_subject || "",
                  body: emailData.html_template || "",
                  pdf_url: emailData.pdf_url || "",
                  pdf_download_url: `${process.env.REACT_APP_API_URL}/quotes/generate-pdf?quote_id=${quotationId}&download=true`,
                  pdf_name: emailData.pdf_name || "Quotation.pdf",
                });

                console.log("Email triggered to Backend!");
                setPendingSentForceUpdate(forceUpdate);
                setIsContantLoading(true);
                setShowEmailSentConfirmModal(true);

                if (mailerTimeoutRef.current)
                  clearTimeout(mailerTimeoutRef.current);
                mailerTimeoutRef.current = setTimeout(() => {
                  console.warn(
                    "⚠️ No response from Electron Mailer App within timeout.",
                  );
                  setIsContantLoading(false);
                  setShowEmailSentConfirmModal(false);
                  setShowDesktopAppError(true);
                }, 4000);
              });
            } catch (error) {
              console.error(
                "Could not connect to Desktop App. Is it running?",
                error,
              );
              message.destroy("opening_mailer_key");
              setShowDesktopAppError(true);
            }
          },
          onError: () => {
            setLoadingAction(null);
            message.destroy("opening_mailer_key");
          },
        }),
      );
      return;
    }

    executeStatusUpdate(status, forceUpdate);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2 flex-wrap gap-2">
        <h3 className="fs-4 fw-bold text-black mb-0">Review</h3>
        {currentLeadTimeWeeks != null && (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-6 fw-semibold text-secondary">
              Estimated Lead Time:
            </span>
            <span className="fs-6 fw-bold text-primary">
              {currentLeadTimeWeeks.toFixed(1)}{" "}
              {currentLeadTimeWeeks === 1 ? "Week" : "Weeks"}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-link p-0 svg-32 ms-1"
                onClick={() => setShowLeadTimeModal(true)}
                title="Edit Estimated Lead Time"
              >
                {svg?.editBtn}
              </button>
            )}
          </div>
        )}
      </div>
      {/* 1. Basic Details */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Basic Details</h4>
        {!readOnly && (
          <span
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => goToStep(CreateQuotationStepsEnum.CUSTOMER_DETAILS)}
          >
            Modify
          </span>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        <div className="row gy-3">
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Customer Type
            </label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot.customer_type === "regular"
                ? "Regular Customer"
                : "New Customer"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Company Name
            </label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot.company_name || "-"}
            </span>
          </div>

          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Contact Person
            </label>
            <span className="fw-semibold text-primary fs-6">
              {CapitalizeFirstWord(
                [snapshot.first_name, snapshot.last_name]
                  .filter(Boolean)
                  .join(" "),
              ) || "-"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">Phone</label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot.phone || "-"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Customer Email
            </label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot.email || "-"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Sage Account Contact Email
            </label>
            <span className="fw-semibold text-primary fs-6 text-break">
              {snapshot.sage_account_contact_email
                ? decryptByText(snapshot.sage_account_contact_email)
                : "-"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              VAT Type
            </label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot.vat_type
                ? getVatValue(snapshot.vat_type)
                : getVatValue(VatType.NOT_VAT_REGISTERED)}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              {" "}
              VAT Percentage
            </label>
            <span className="fw-semibold text-primary fs-6">
              {snapshot?.tax_id?.name && !isCustomerLoggedIn() ? (
                <>
                  {snapshot?.tax_id?.name ? snapshot?.tax_id?.name : ""}{" "}
                  {`(${
                    snapshot?.tax_id?.tax_rate !== undefined &&
                    snapshot?.tax_id?.tax_rate !== null
                      ? `${snapshot?.tax_id?.tax_rate}%`
                      : ""
                  })`}{" "}
                  {`(${
                    snapshot?.tax_id?.code
                      ? `Code : ${snapshot?.tax_id?.code}`
                      : ""
                  })`}
                </>
              ) : snapshot?.vat_percentage !== undefined &&
                snapshot?.vat_percentage !== null ? (
                `${snapshot.vat_percentage}%`
              ) : (
                "-"
              )}
            </span>
          </div>
          {(!isCustomerLoggedIn() || snapshot?.show_retail_price !== false) && (
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Markup / Discount
              </label>
              <span className="fw-semibold fs-6">
                {Number(snapshot?.margin) > 0 ? (
                  <span className="text-success d-inline-flex align-items-center gap-1">
                    <span>Markup :</span>
                    <span className="fw-bold">{snapshot?.margin}%</span>
                  </span>
                ) : Number(snapshot?.discount) > 0 ? (
                  <span className="text-danger d-inline-flex align-items-center gap-1">
                    <span>Discount :</span>
                    <span className="fw-bold">{snapshot?.discount}%</span>
                  </span>
                ) : (
                  <span className="text-muted">0%</span>
                )}
              </span>
            </div>
          )}
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Salesperson
            </label>
            <span className="fw-semibold text-primary fs-6">
              {getStaticSalespersonLabel(snapshot.salesperson_id) ||
                (snapshot.salesperson_id
                  ? getDecryptedName(snapshot.salesperson_id)
                  : snapshot.salesperson_name || "-")}
            </span>
          </div>

          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Billing Address
            </label>
            <span className="fw-semibold text-primary fs-6">
              {formatAddress(snapshot.billing_address)}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Delivery / Installation Address
            </label>
            <span className="fw-semibold text-primary fs-6">
              {formatDeliveryAddress(snapshot.delivery_address)}
            </span>
          </div>
          {!isCustomerLoggedIn() && (
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Show Retail Price
              </label>
              <span className="fw-semibold text-primary fs-6">
                {showRetailPriceKey ? "Yes" : "No"}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Account Contact Details */}
      {snapshot?.additional_account_contact_id &&
        typeof snapshot.additional_account_contact_id === "object" && (
          <div className="row gy-3 mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fs-4 fw-bold text-primary mb-0">
                  Account Contact Details
                </h4>
              </div>
              <div className="card border-0 shadow-sm p-3 p-md-3 rounded-4 bg-white">
                <div className="row gy-2">
                  <div className="col-sm-4">
                    <label className="fs-6 fw-normal text-949494 d-block">
                      Full Name
                    </label>
                    <span className="fw-semibold text-primary fs-6">
                      {CapitalizeFirstWord(
                        getDecryptedName(
                          snapshot.additional_account_contact_id,
                        ),
                      ) || "-"}
                    </span>
                  </div>
                  <div className="col-sm-4">
                    <label className="fs-6 fw-normal text-949494 d-block">
                      Phone
                    </label>
                    <span className="fw-semibold text-primary fs-6">
                      {snapshot.additional_account_contact_id.phone || "-"}
                    </span>
                  </div>
                  <div className="col-sm-4">
                    <label className="fs-6 fw-normal text-949494 d-block">
                      Email
                    </label>
                    <span className="fw-semibold text-primary fs-6 text-break">
                      {snapshot.additional_account_contact_id.email
                        ? decryptByText(
                            snapshot.additional_account_contact_id.email,
                          )
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      {/* Payment Milestones */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Payment Milestones</h4>
        {!readOnly && (
          <span
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => goToStep(CreateQuotationStepsEnum.CUSTOMER_DETAILS)}
          >
            Modify
          </span>
        )}
      </div>
      <div className="card d-flex flex-column gap-3 border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {milestones.length > 0 ? (
          milestones.map((item: any) => {
            const isPercentage = item.value_base === "percentage";
            const displayStatus = item.status;
            return (
              <div key={item._id} className="border px-3 py-2 rounded-2">
                <div className="row gy-2">
                  <div className="col-md-4 col-sm-6">
                    <label className="fs-6 fw-semibold">
                      {item.milestone_name}
                    </label>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <span className="fs-6 fw-semibold">
                      Payment -{" "}
                      {isPercentage
                        ? `${item.value}%`
                        : formatCurrency(
                            item.value,
                            quotationDetails?.currency,
                          )}
                      {item.calculated_amount > 0 && isPercentage && (
                        <span className="ms-1">
                          {`(${formatCurrency(
                            item.calculated_amount,
                            quotationDetails?.currency,
                          )})`}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-muted py-3">
            No payment milestones configured.
          </div>
        )}
      </div>
      {/* Preferences */}
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Preferences</h4>
        {!(readOnly) && (
<span
          className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
          onClick={() => goToStep(CreateQuotationStepsEnum.CUSTOMER_DETAILS)}
        >
          Modify
        </span>
)}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        <div className="row gy-3">
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Color Preference
            </label>
            <span className="fw-semibold text-primary fs-6">
              {preferences.color || "-"}
            </span>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block">
              Include Design Preview
            </label>
            <span className="fw-semibold text-primary fs-6">
              {preferences.design_preview ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </div> */}
      {/* Additional Information */}
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">
          Additional Information
        </h4>
        {!(readOnly) && (
<span
          className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
          onClick={() => goToStep(CreateQuotationStepsEnum.ADDITIONAL_INFO)}
        >
          Modify
        </span>
)}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        <div className="row gy-3">
          <div className="col-lg-3 col-md-4 col-sm-6">
            <label className="fs-6 fw-normal text-949494 d-block mb-1">
              {data?.categories_snapshot?.product_type_name || "Pallets Rack"}{" "}
              Type
            </label>
            <div className="d-flex align-items-center gap-2">
              {data.categories_snapshot?.image && (
                <img
                  src={getFullAwsUrl(data.categories_snapshot.image, true)}
                  alt="Category"
                  width={30}
                  height={30}
                  className="rounded-1 object-fit-cover border"
                />
              )}
              <span className="fw-semibold text-dark fs-6 lh-m">
                {data.categories_snapshot?.name || "-"}
              </span>
            </div>
          </div>

          {loadingFields ? (
            <div className="col-12 py-3">
              <Spin size="small" tip="Loading additional info..." />
            </div>
          ) : (
            additionalInfoFields.map((field) => {
              const val = additionalInfo[field.key];
              const hasValue =
                val !== undefined &&
                val !== null &&
                (Array.isArray(val) ? val.length > 0 : true);

              return (
                <div key={field._id} className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-949494 d-block">
                    {field.name}
                  </label>
                  <div className="d-flex flex-column">
                    {!hasValue ? (
                      <span className="fw-semibold text-dark fs-6">-</span>
                    ) : Array.isArray(val) ? (
                      val.map((v) => (
                        <React.Fragment key={v}>
                          {renderDynamicInfoItem(field, v)}
                        </React.Fragment>
                      ))
                    ) : (
                      renderDynamicInfoItem(field, val)
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div> */}
      {/* Grouped Categories */}
      {Array.isArray(data.products) &&
        data.products.map((categoryGroup: any, catIndex: number) => {
          const categorySnap = (data.categories_snapshot || []).find(
            (c: any) =>
              c.category_id === categoryGroup.category_id ||
              c._id === categoryGroup.category_id,
          );

          const catProducts = categoryGroup.products || [];
          const catAccessories = categoryGroup.accessories || [];
          const catReplacementParts = categoryGroup.replacement_parts || [];

          const catSummary = categoryGroup.summery || [];
          const slug = categorySnap?.slug as CategorySlugs;
          const fieldsArray = SummaryFieldsSchema[slug] || [];
          const label = SummaryBlockLabel[slug];

          const isMezzanine =
            categorySnap?.slug?.toLowerCase().includes("mezzanine") ||
            categorySnap?.name?.toLowerCase().includes("mezzanine") ||
            categorySnap?.product_type_name
              ?.toLowerCase()
              .includes("mezzanine");

          if (
            !isMezzanine &&
            catProducts.length === 0 &&
            catAccessories.length === 0 &&
            catReplacementParts.length === 0
            // && catSummary.length === 0
          )
            return null;

          const catTotalAmount =
            (categoryGroup.products_total_amount || 0) +
            (categoryGroup.accessories_total_amount || 0) +
            (categoryGroup.replacement_parts_total_amount || 0) +
            (isMezzanine ? data?.mezzanine_floor?.total_cost || 0 : 0);

          return (
            <div
              key={categoryGroup._id || catIndex}
              className="mb-4 p-md-4 p-2 rounded-4 card border-0 shadow"
              style={{ backgroundColor: "#E1E1E1" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 pb-3 border-bottom border-secondary-subtle">
                <div className="d-flex align-items-center gap-2">
                  {categorySnap?.image && (
                    <img
                      src={getFullAwsUrl(categorySnap.image, true)}
                      alt={categorySnap?.name}
                      width={24}
                      height={24}
                      className="rounded-1 object-fit-cover"
                    />
                  )}
                  <h4 className="fs-5 fw-bold text-dark mb-0">
                    {categorySnap?.name || "Category"}
                  </h4>
                  {!readOnly && (
                    <span
                      className="fs-7 fw-bold text-dark cursor-pointer d-flex align-items-center gap-1 ms-2"
                      onClick={() =>
                        goToStep(CreateQuotationStepsEnum.STORAGE_TYPES)
                      }
                    >
                      {svg?.changeIcon}{" "}
                      <span className="border-bottom border-dark">Change</span>
                    </span>
                  )}
                </div>
                <div className="fs-5 text-dark text-end">
                  {categorySnap?.name || "Category"} Total Amount:{" "}
                  <span className="fw-bold fs-5 ms-1">
                    {formatCurrency(catTotalAmount, quotationDetails?.currency)}
                  </span>
                </div>
              </div>
              {/* Floor Details for Mezzanine */}
              {isMezzanine && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fs-5 fw-bold text-dark mb-0">
                      Floor Details
                    </h5>
                    {!readOnly && (
                      <span
                        className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set(
                            "step",
                            `${categorySnap?._id}__floor-details`,
                          );
                          navigate({ search: params.toString() });
                        }}
                      >
                        Modify
                      </span>
                    )}
                  </div>
                  <EstimationFloorDetails readOnly={true} data={data} />
                </div>
              )}
              {/* Products */}
              {catProducts.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fs-5 fw-bold text-dark mb-0">Products</h5>
                    {!readOnly && (
                      <span
                        className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set(
                            "step",
                            `${categorySnap?._id}__${CreateQuotationStepsEnum.PRODUCTS}`,
                          );
                          navigate({ search: params.toString() });
                        }}
                      >
                        Modify
                      </span>
                    )}
                  </div>
                  <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-3 p-2 rounded-4 bg-white">
                    {catProducts.map((parentProduct: any) => {
                      // const majorProductType = getMajorProductType(
                      //   parentProduct?.component_type,
                      // );
                      return parentProduct.variants?.map(
                        (variant: any, vIdx: number) => (
                          <div
                            key={`${parentProduct.product_id}-${vIdx}`}
                            className="card border shadow-sm rounded-3"
                          >
                            <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                              <div className="row gy-sm-3 gy-2 align-items-lg-center">
                                <div className="col-12">
                                  <div className="d-flex gap-md-3 gap-2">
                                    <div className="img-60">
                                      {(() => {
                                        const validImages = [
                                          parentProduct?.pre_galvanised_image,
                                          parentProduct?.hot_dip_galvanised_image,
                                          parentProduct?.powder_coated_image,
                                        ].filter(Boolean);

                                        if (validImages.length === 0) {
                                          return (
                                            <Image
                                              src={placeHolderImage}
                                              alt={parentProduct.name}
                                              width="50"
                                              height="50"
                                              preview={false}
                                              rootClassName="w-100 h-100"
                                              className="rounded-2 object-fit-cover border w-100 h-100"
                                            />
                                          );
                                        }

                                        return (
                                          <Image.PreviewGroup>
                                            {validImages.map(
                                              (imgKey, index) => (
                                                <Image
                                                  key={imgKey}
                                                  src={getFullAwsUrl(imgKey)}
                                                  alt={parentProduct.name}
                                                  width={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  height={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  fallback={placeHolderImage}
                                                  rootClassName={
                                                    index === 0
                                                      ? "w-100 h-100"
                                                      : "d-none"
                                                  }
                                                  className={
                                                    index === 0
                                                      ? "rounded-2 object-fit-cover border w-100 h-100"
                                                      : ""
                                                  }
                                                  style={{
                                                    display:
                                                      index === 0
                                                        ? undefined
                                                        : "none",
                                                  }}
                                                  preview={
                                                    index === 0
                                                      ? {
                                                          mask: <EyeOutlined />,
                                                        }
                                                      : undefined
                                                  }
                                                />
                                              ),
                                            )}
                                          </Image.PreviewGroup>
                                        );
                                      })()}
                                    </div>
                                    <div className="d-flex flex-column justify-content-center gap-1 w-100">
                                      <div className="d-flex justify-content-between align-items-start gap-2">
                                        <h6 className="fw-bold mb-0 text-dark fs-6">
                                          {variant.variant_name}
                                        </h6>
                                        {!isRetailPriceVisible && (
                                          <div className="text-center text-md-end ms-auto pe-lg-3">
                                            <small
                                              className="text-muted d-block fw-normal lh-1"
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {parentProduct?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold lh-1 fs-6">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <small
                                        className="text-muted fw-normal"
                                        style={{ fontSize: "12px" }}
                                      >
                                        {parentProduct?.description && (
                                          <DescriptionWithShowMore
                                            description={
                                              parentProduct?.description
                                            }
                                          />
                                        )}
                                        {variant?.max_load_per_bay && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Bay:{" "}
                                            {variant.max_load_per_bay}
                                          </div>
                                        )}
                                        {variant?.max_load_per_pair && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Pair:{" "}
                                            {variant.max_load_per_pair}
                                          </div>
                                        )}
                                        {variant?.max_load_per_shelf && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Shelf:{" "}
                                            {variant.max_load_per_shelf}
                                          </div>
                                        )}
                                      </small>
                                      {Number(
                                        variant?.is_bom ??
                                          parentProduct?.is_bom,
                                      ) === 2 &&
                                        Boolean(
                                          variant?.assembly_drawing_require ??
                                            parentProduct?.assembly_drawing_require,
                                        ) && (
                                          <div className="my-1">
                                            <Tag
                                              color="default"
                                              className="rounded-pill px-3 py-0.5 fw-semibold"
                                              style={{ fontSize: "12px" }}
                                            >
                                              Factory Assembled -{" "}
                                              {variant?.factory_assembled ??
                                              parentProduct?.factory_assembled
                                                ? "Yes"
                                                : "No"}
                                            </Tag>
                                          </div>
                                        )}
                                      <div className="row gy-2 gx-4 gx-lg-5 justify-content-between text-start text-md-start">
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal">
                                                  Retail(RRP):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <small className="text-muted d-block fw-normal text-nowrap">
                                              {parentProduct?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Retail(RRP) Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.total_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discount (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discount_amount,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Unit Price (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discounted_unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.line_total,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isInstallationRequired &&
                                        variant.installation_mark_up_amount &&
                                        isRetailPriceVisible ? (
                                          <div className="col-lg-auto col-md-auto col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Installation Cost(
                                                  {variant.installation_mark_up_percentage ||
                                                    5}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.installation_mark_up_amount,
                                                    quotationDetails?.currency,
                                                    quotationDetails?.conversion_rate,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {variant?.drawings?.length > 0 && (
                              <div className="p-2">
                                <AttachedDrawings
                                  drawings={variant?.drawings}
                                  setPreviewFiles={setPreviewFiles}
                                  setPreviewIndex={setPreviewIndex}
                                  setPreviewModal={setPreviewModalOpen}
                                />
                              </div>
                            )}
                          </div>
                        ),
                      );
                    })}
                    <div className="text-end fs-6 mt-2">
                      Products Total Amount:{" "}
                      <span className="fw-bold">
                        {formatCurrency(
                          categoryGroup.products_total_amount,
                          quotationDetails?.currency,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Accessories */}
              {catAccessories.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fs-5 fw-bold text-dark mb-0">Accessories</h5>
                    {!readOnly && (
                      <span
                        className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set(
                            "step",
                            `${categorySnap?._id}__${CreateQuotationStepsEnum.ACCESSORIES}`,
                          );
                          navigate({ search: params.toString() });
                        }}
                      >
                        Modify
                      </span>
                    )}
                  </div>
                  <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-3 p-2 rounded-4 bg-white">
                    {catAccessories.map((parentAccessory: any) => {
                      return parentAccessory.variants?.map(
                        (variant: any, vIdx: number) => (
                          <div
                            key={`${parentAccessory.accessory_id}-${vIdx}`}
                            className="card border shadow-sm rounded-3"
                          >
                            <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                              <div className="row gy-sm-3 gy-2 align-items-lg-center">
                                <div className="col-12">
                                  <div className="d-flex gap-md-3 gap-2">
                                    <div className="img-60">
                                      {(() => {
                                        const validImages = [
                                          parentAccessory?.pre_galvanised_image,
                                          parentAccessory?.hot_dip_galvanised_image,
                                          parentAccessory?.powder_coated_image,
                                        ].filter(Boolean);

                                        if (validImages.length === 0) {
                                          return (
                                            <Image
                                              src={placeHolderImage}
                                              alt={parentAccessory.name}
                                              width="50"
                                              height="50"
                                              preview={false}
                                              rootClassName="w-100 h-100"
                                              className="rounded-2 object-fit-cover border w-100 h-100"
                                            />
                                          );
                                        }

                                        return (
                                          <Image.PreviewGroup>
                                            {validImages.map(
                                              (imgKey, index) => (
                                                <Image
                                                  key={imgKey}
                                                  src={getFullAwsUrl(imgKey)}
                                                  alt={parentAccessory.name}
                                                  width={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  height={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  fallback={placeHolderImage}
                                                  rootClassName={
                                                    index === 0
                                                      ? "w-100 h-100"
                                                      : "d-none"
                                                  }
                                                  className={
                                                    index === 0
                                                      ? "rounded-2 object-fit-cover border w-100 h-100"
                                                      : ""
                                                  }
                                                  style={{
                                                    display:
                                                      index === 0
                                                        ? undefined
                                                        : "none",
                                                  }}
                                                  preview={
                                                    index === 0
                                                      ? {
                                                          mask: <EyeOutlined />,
                                                        }
                                                      : undefined
                                                  }
                                                />
                                              ),
                                            )}
                                          </Image.PreviewGroup>
                                        );
                                      })()}
                                    </div>
                                    <div className="d-flex flex-column justify-content-center gap-1 w-100">
                                      <div className="d-flex justify-content-between align-items-start gap-2">
                                        <h6 className="fw-bold mb-0 text-dark fs-6">
                                          {variant.variant_name}
                                        </h6>
                                        {!isRetailPriceVisible && (
                                          <div className="text-center text-md-end ms-auto pe-lg-3">
                                            <small
                                              className="text-muted d-block fw-normal lh-1"
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {parentAccessory?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold lh-1 fs-6">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <small
                                        className="text-muted fw-normal"
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        {parentAccessory?.description && (
                                          <div className="text-wrap lh-tight mb-1">
                                            <DescriptionWithShowMore
                                              description={
                                                parentAccessory.description
                                              }
                                            />
                                          </div>
                                        )}
                                        {variant?.max_load_per_bay && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Bay:{" "}
                                            {variant.max_load_per_bay}
                                          </div>
                                        )}
                                        {variant?.max_load_per_pair && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Pair:{" "}
                                            {variant.max_load_per_pair}
                                          </div>
                                        )}
                                        {variant?.max_load_per_shelf && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Shelf:{" "}
                                            {variant.max_load_per_shelf}
                                          </div>
                                        )}
                                      </small>
                                      {Number(
                                        variant?.is_bom ??
                                          parentAccessory?.is_bom,
                                      ) === 2 &&
                                        Boolean(
                                          variant?.assembly_drawing_require ??
                                            parentAccessory?.assembly_drawing_require,
                                        ) && (
                                          <div className="my-1">
                                            <Tag
                                              color="default"
                                              className="rounded-pill px-3 py-0.5 fw-semibold"
                                              style={{ fontSize: "12px" }}
                                            >
                                              Factory Assembled -{" "}
                                              {variant?.factory_assembled ??
                                              parentAccessory?.factory_assembled
                                                ? "Yes"
                                                : "No"}
                                            </Tag>
                                          </div>
                                        )}
                                      <div className="row gy-2 gx-4 gx-lg-5 justify-content-between text-start text-md-start">
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Retail(RRP):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <small className="text-muted d-block fw-normal text-nowrap">
                                              {parentAccessory?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Retail(RRP) Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.total_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discount (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discount_amount,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Unit Price (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discounted_unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.line_total,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isInstallationRequired &&
                                        variant.installation_mark_up_amount &&
                                        isRetailPriceVisible ? (
                                          <div className="col-lg-auto col-md-auto col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Installation Cost(
                                                  {variant.installation_mark_up_percentage ||
                                                    5}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.installation_mark_up_amount,
                                                    quotationDetails?.currency,
                                                    quotationDetails?.conversion_rate,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {variant?.drawings?.length > 0 && (
                              <div className="p-2">
                                <AttachedDrawings
                                  drawings={variant?.drawings}
                                  setPreviewFiles={setPreviewFiles}
                                  setPreviewIndex={setPreviewIndex}
                                  setPreviewModal={setPreviewModalOpen}
                                />
                              </div>
                            )}
                          </div>
                        ),
                      );
                    })}
                    <div className="text-end fs-6 mt-2">
                      Accessories Total Amount:{" "}
                      <span className="fw-bold">
                        {formatCurrency(
                          categoryGroup.accessories_total_amount,
                          quotationDetails?.currency,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Replacement Parts */}
              {catReplacementParts.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fs-5 fw-bold text-dark mb-0">
                      Replacement Parts
                    </h5>
                    {!readOnly && (
                      <span
                        className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set(
                            "step",
                            `${categorySnap?._id}__${CreateQuotationStepsEnum.ACCESSORIES}`,
                          );
                          navigate({ search: params.toString() });
                        }}
                      >
                        Modify
                      </span>
                    )}
                  </div>
                  <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-3 p-2 rounded-4 bg-white">
                    {catReplacementParts.map((parentReplacement: any) => {
                      return parentReplacement.variants?.map(
                        (variant: any, vIdx: number) => (
                          <div
                            key={`${parentReplacement.replaceparts_id}-${vIdx}`}
                            className="card border shadow-sm rounded-3"
                          >
                            <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                              <div className="row gy-sm-3 gy-2 align-items-lg-center">
                                <div className="col-12">
                                  <div className="d-flex gap-md-3 gap-2">
                                    <div className="img-60">
                                      {(() => {
                                        const validImages = [
                                          parentReplacement?.pre_galvanised_image,
                                          parentReplacement?.hot_dip_galvanised_image,
                                          parentReplacement?.powder_coated_image,
                                        ].filter(Boolean);

                                        if (validImages.length === 0) {
                                          return (
                                            <Image
                                              src={placeHolderImage}
                                              alt={parentReplacement.name}
                                              width="50"
                                              height="50"
                                              preview={false}
                                              rootClassName="w-100 h-100"
                                              className="rounded-2 object-fit-cover border w-100 h-100"
                                            />
                                          );
                                        }

                                        return (
                                          <Image.PreviewGroup>
                                            {validImages.map(
                                              (imgKey, index) => (
                                                <Image
                                                  key={imgKey}
                                                  src={getFullAwsUrl(imgKey)}
                                                  alt={parentReplacement.name}
                                                  width={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  height={
                                                    index === 0
                                                      ? "50"
                                                      : undefined
                                                  }
                                                  fallback={placeHolderImage}
                                                  rootClassName={
                                                    index === 0
                                                      ? "w-100 h-100"
                                                      : "d-none"
                                                  }
                                                  className={
                                                    index === 0
                                                      ? "rounded-2 object-fit-cover border w-100 h-100"
                                                      : ""
                                                  }
                                                  style={{
                                                    display:
                                                      index === 0
                                                        ? undefined
                                                        : "none",
                                                  }}
                                                  preview={
                                                    index === 0
                                                      ? {
                                                          mask: <EyeOutlined />,
                                                        }
                                                      : undefined
                                                  }
                                                />
                                              ),
                                            )}
                                          </Image.PreviewGroup>
                                        );
                                      })()}
                                    </div>
                                    <div className="d-flex flex-column justify-content-center gap-1 w-100">
                                      <div className="d-flex justify-content-between align-items-start gap-2">
                                        <h6 className="fw-bold mb-0 text-dark fs-6">
                                          {variant.variant_name}
                                        </h6>
                                        {!isRetailPriceVisible && (
                                          <div className="text-center text-md-end ms-auto pe-lg-3">
                                            <small
                                              className="text-muted d-block fw-normal lh-1"
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {parentReplacement?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold lh-1 fs-6">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <small
                                        className="text-muted fw-normal"
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        {parentReplacement?.description && (
                                          <div className="text-wrap lh-tight mb-1">
                                            <DescriptionWithShowMore
                                              description={
                                                parentReplacement.description
                                              }
                                            />
                                          </div>
                                        )}
                                        {variant?.max_load_per_bay && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Bay:{" "}
                                            {variant.max_load_per_bay}
                                          </div>
                                        )}
                                        {variant?.max_load_per_pair && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Pair:{" "}
                                            {variant.max_load_per_pair}
                                          </div>
                                        )}
                                        {variant?.max_load_per_shelf && (
                                          <div className="lh-tight mb-1">
                                            Max Load per Shelf:{" "}
                                            {variant.max_load_per_shelf}
                                          </div>
                                        )}
                                      </small>
                                      {Number(
                                        variant?.is_bom ??
                                          parentReplacement?.is_bom,
                                      ) === 2 &&
                                        Boolean(
                                          variant?.assembly_drawing_require ??
                                            parentReplacement?.assembly_drawing_require,
                                        ) && (
                                          <div className="my-1">
                                            <Tag
                                              color="default"
                                              className="rounded-pill px-3 py-0.5 fw-semibold"
                                              style={{ fontSize: "12px" }}
                                            >
                                              Factory Assembled -{" "}
                                              {variant?.factory_assembled ??
                                              parentReplacement?.factory_assembled
                                                ? "Yes"
                                                : "No"}
                                            </Tag>
                                          </div>
                                        )}
                                      <div className="row gy-2 gx-4 gx-lg-5 justify-content-between text-start text-md-start">
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Retail(RRP):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <small className="text-muted d-block fw-normal text-nowrap">
                                              {parentReplacement?.labelInQuotation ||
                                                "Qty"}
                                              :
                                            </small>
                                            <span className="fw-bold">
                                              {variant.quantity}
                                            </span>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Retail(RRP) Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.total_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-6">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discount (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discount_amount,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Unit Price (
                                                  {variant.discount_percentage ||
                                                    0}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.discounted_unit_price,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isRetailPriceVisible && (
                                          <div className="col-lg-auto col-md-3 col-sm-4 col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Total:
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.line_total,
                                                    quotationDetails?.currency,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {isInstallationRequired &&
                                        variant.installation_mark_up_amount &&
                                        isRetailPriceVisible ? (
                                          <div className="col-lg-auto col-md-auto col-12">
                                            <div className="d-flex align-items-start gap-1">
                                              <div>
                                                <small className="text-muted d-block fw-normal text-nowrap">
                                                  Discounted Installation Cost(
                                                  {variant.installation_mark_up_percentage ||
                                                    5}
                                                  %):
                                                </small>
                                                <span className="fw-bold">
                                                  {formatCurrency(
                                                    variant.installation_mark_up_amount,
                                                    quotationDetails?.currency,
                                                    quotationDetails?.conversion_rate,
                                                  )}
                                                </span>
                                              </div>
                                              {showHiddenRetailPriceTooltip && (
                                                <Tooltip
                                                  title={
                                                    RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT
                                                  }
                                                >
                                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                                    {svg?.restrictedAvatarIcon}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {variant?.drawings?.length > 0 && (
                              <div className="p-2">
                                <AttachedDrawings
                                  drawings={variant?.drawings}
                                  setPreviewFiles={setPreviewFiles}
                                  setPreviewIndex={setPreviewIndex}
                                  setPreviewModal={setPreviewModalOpen}
                                />
                              </div>
                            )}
                          </div>
                        ),
                      );
                    })}
                    <div className="text-end fs-6 mt-2">
                      Replacement Parts Total Amount:{" "}
                      <span className="fw-bold">
                        {formatCurrency(
                          categoryGroup.replacement_parts_total_amount,
                          quotationDetails?.currency,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Section */}
              {/* {label && fieldsArray.length > 0 && catSummary.length > 0 && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fs-5 fw-bold text-dark mb-0">{label}s</h5>
                    {!(readOnly) && (
<span
                      className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.set(
                          "step",
                          `${categorySnap?._id}__${CreateQuotationStepsEnum.SUMMARY}`,
                        );
                        navigate({ search: params.toString() });
                      }}
                    >
                      Modify
                    </span>
)}
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {catSummary.map((item: any, idx: number) => (
                      <div
                        key={item._id || idx}
                        className="card border-0 shadow-sm p-md-3 p-2 rounded-4 bg-white"
                      >
                        <div className="card-body py-1 px-2 fs-6 fw-semibold text-dark">
                          <div className="text-black fs-6 fw-bold mb-3 border-bottom pb-2">
                            {label} {idx + 1}
                          </div>
                          {slug ===
                          CategorySlugs.ADJUSTABLE_PALLET_RACKING_APR ? (
                            <APRView item={item} />
                          ) : (
                            <div className="row g-md-4 g-3">
                              {fieldsArray.map((field) => {
                                const val = item[field.key];
                                if (
                                  val === undefined ||
                                  val === null ||
                                  val === ""
                                )
                                  return null;

                                return (
                                  <div
                                    className="col-lg-3 col-md-4 col-6"
                                    key={field.key}
                                  >
                                    <label
                                      className="text-muted d-block fw-normal mb-1"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {field.label}
                                    </label>
                                    <span
                                      className="fw-bold text-dark"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {typeof val === "object"
                                        ? val?.label
                                        : val}{" "}
                                      {field.unit}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )} */}
            </div>
          );
        })}

      {/* Bespoke / Bought-In Items */}
      {data?.bespoke_items && data.bespoke_items.length > 0 && (
        <div className="mb-4 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fs-5 fw-bold text-dark mb-0">
              Bespoke / Bought-In Items
            </h5>
            {!readOnly && (
              <span
                className="fs-7 fw-bold text-decoration-underline text-dark cursor-pointer"
                onClick={() => goToStep(CreateQuotationStepsEnum.BESPOKE_ITEMS)}
              >
                Modify
              </span>
            )}
          </div>
          <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-3 p-2 rounded-4 bg-white">
            {data.bespoke_items.map((item: any, idx: number) => {
              const key = item._id || item.bespoke_item_id || idx;
              const costVal = Number(item.cost_price) || 0;
              const itemMkVal = Number(item.markup) || 0;
              const qtyVal = Number(item.qty) || 1;
              const quotationMarginVal = Number(
                quotationDetails?.margin !== undefined &&
                  quotationDetails?.margin !== null &&
                  quotationDetails?.margin !== ""
                  ? quotationDetails.margin
                  : quotationDetails?.customer_snapshot?.margin !== undefined &&
                    quotationDetails?.customer_snapshot?.margin !== null &&
                    quotationDetails?.customer_snapshot?.margin !== ""
                  ? quotationDetails.customer_snapshot.margin
                  : (quotationDetails?.customer_id as any)?.margin || 0,
              );
              const quotationDiscountVal = Number(
                quotationDetails?.discount !== undefined &&
                  quotationDetails?.discount !== null &&
                  quotationDetails?.discount !== ""
                  ? quotationDetails.discount
                  : quotationDetails?.customer_snapshot?.discount || 0,
              );

              const costAfterItemMk = costVal * (1 + itemMkVal / 100);
              const unitPrice = Number(
                (costAfterItemMk * (1 + quotationMarginVal / 100)).toFixed(2),
              );
              const basicTotal = Number((unitPrice * qtyVal).toFixed(2));
              const discountAmount = Number(
                ((basicTotal * quotationDiscountVal) / 100).toFixed(2),
              );
              const finalTotal = Number(
                (basicTotal - discountAmount).toFixed(2),
              );

              return (
                <div key={key} className="card border shadow-sm rounded-3">
                  <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                    <div className="row gy-sm-3 gy-2 align-items-lg-center">
                      <div className="col-12">
                        <div className="d-flex gap-md-3 gap-2">
                          <div className="img-60 flex-shrink-0">
                            <Image
                              src={item.image || placeHolderImage}
                              alt={item.name}
                              width="50"
                              height="50"
                              preview={false}
                              rootClassName="w-100 h-100"
                              className="rounded-2 object-fit-cover border w-100 h-100"
                              fallback={placeHolderImage}
                            />
                          </div>
                          <div className="d-flex flex-column justify-content-center gap-1 w-100">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <h6 className="fw-bold mb-0 text-dark fs-6">
                                {item.name || "Bespoke Item"}
                              </h6>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {item.supplier_name && (
                                  <span className="badge bg-light text-dark border px-2 py-1 fs-7 fw-normal">
                                    Supplier: {item.supplier_name}
                                  </span>
                                )}
                                {item.supplier_quote_no && (
                                  <span className="badge bg-light text-secondary border px-2 py-1 fs-7 fw-normal">
                                    Quote No: {item.supplier_quote_no}
                                  </span>
                                )}
                              </div>
                            </div>
                            {item.description && (
                              <small
                                className="text-muted fw-normal text-wrap d-block lh-tight mt-1"
                                style={{ fontSize: "0.85rem" }}
                              >
                                {item.description}
                              </small>
                            )}

                            {/* Process Instructions */}
                            {item.process_instructions && (
                              <>
                                <hr className="my-2 text-muted opacity-25" />
                                <p
                                  className="mb-0 text-dark fw-semibold"
                                  style={{
                                    fontSize: "0.9rem",
                                    lineHeight: 1.5,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {item.process_instructions}
                                </p>
                              </>
                            )}

                            {/* Product-style Metrics Row */}
                            <div className="row gy-2 text-start text-md-start mt-2">
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Retail(RRP):
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          unitPrice,
                                          quotationDetails?.currency,
                                          quotationDetails?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Retail(RRP) Total:
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          basicTotal,
                                          quotationDetails?.currency,
                                          quotationDetails?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-6">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Discount ({quotationDiscountVal}%):
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          discountAmount,
                                          quotationDetails?.currency,
                                          quotationDetails?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                              {isRetailPriceVisible && (
                                <div className="col-lg-2 col-md-3 col-sm-4 col-12">
                                  <div className="d-flex align-items-start gap-1">
                                    <div>
                                      <small className="text-muted d-block fw-normal">
                                        Total:
                                      </small>
                                      <span className="fw-bold">
                                        {formatCurrency(
                                          finalTotal,
                                          quotationDetails?.currency,
                                          quotationDetails?.conversion_rate || 1,
                                        )}
                                      </span>
                                    </div>
                                    {showHiddenRetailPriceTooltip && (
                                      <Tooltip
                                        title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                      >
                                        <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                          {svg?.restrictedAvatarIcon}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="text-end fs-5 mt-2 me-2">
              Bespoke Items Total Amount:{" "}
              <span className="fw-bold text-primary">
                {formatCurrency(
                  Array.isArray(data?.bespoke_items)
                    ? data.bespoke_items.reduce((sum: number, item: any) => {
                        const costVal = Number(item.cost_price) || 0;
                        const itemMkVal = Number(item.markup) || 0;
                        const qtyVal = Number(item.qty) || 1;
                        const qMargin = Number(
                          quotationDetails?.margin !== undefined &&
                            quotationDetails?.margin !== null &&
                            quotationDetails?.margin !== ""
                            ? quotationDetails.margin
                            : quotationDetails?.customer_snapshot?.margin !==
                                undefined &&
                              quotationDetails?.customer_snapshot?.margin !==
                                null &&
                              quotationDetails?.customer_snapshot?.margin !== ""
                            ? quotationDetails.customer_snapshot.margin
                            : (quotationDetails?.customer_id as any)?.margin ||
                              0,
                        );
                        const qDiscount = Number(
                          quotationDetails?.discount !== undefined &&
                            quotationDetails?.discount !== null &&
                            quotationDetails?.discount !== ""
                            ? quotationDetails.discount
                            : quotationDetails?.customer_snapshot?.discount ||
                                0,
                        );
                        const costAfterItemMk = costVal * (1 + itemMkVal / 100);
                        const unitPrice = Number(
                          (costAfterItemMk * (1 + qMargin / 100)).toFixed(2),
                        );
                        const basicTotal = Number(
                          (unitPrice * qtyVal).toFixed(2),
                        );
                        const discountAmount = Number(
                          ((basicTotal * qDiscount) / 100).toFixed(2),
                        );
                        return (
                          sum + Number((basicTotal - discountAmount).toFixed(2))
                        );
                      }, 0)
                    : data?.bespoke_items_total ?? 0,
                  quotationDetails?.currency,
                  quotationDetails?.conversion_rate || 1,
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Services */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Services</h4>
        {!readOnly && (
          <span
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => goToStep(CreateQuotationStepsEnum.SERVICES)}
          >
            Modify
          </span>
        )}
      </div>
      <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {serviceList.length > 0 ? (
          serviceList.map((service: any, index: number) => {
            const pricingModel = service.pricing_model;
            const isDayPerson =
              pricingModel === ServicePriceType.DAY_PERSON ||
              pricingModel === "per_day_per_person";
            const isDay =
              pricingModel === ServicePriceType.DAY ||
              pricingModel === "per_day";
            const isCustom =
              pricingModel === ServicePriceType.CUSTOM ||
              pricingModel === "custom";
            const showPerson = isDayPerson;
            const showDays = isDayPerson || isDay;
            const isFactoryAssembledFrames =
              service?.slug === "factory-assembled-frames" ||
              service?.service_id === "69649a195c96859b4ad602ac" ||
              service?.name?.toLowerCase() === "factory assembled frames";
            const showQty =
              !isCustom && !isDayPerson && !isFactoryAssembledFrames;
            const displayPriceType =
              PriceTypeLabels[service.pricing_model as ServicePriceType] ||
              service.pricing_model;

            return (
              <div key={service._id || index} className="card border rounded-3">
                <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                  <div className="row align-items-center gy-2">
                    <div className="col-md-6 col-12">
                      <div className="d-flex align-items-center gap-2">
                        <div className="img-60">
                          <Image
                            src={
                              service.image
                                ? getFullAwsUrl(service.image)
                                : placeHolderImage
                            }
                            width={50}
                            height={50}
                            className="rounded-2 object-fit-cover border w-100 h-100"
                            rootClassName="w-100 h-100"
                            preview={{ mask: <EyeOutlined /> }}
                            alt={service.name}
                          />
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0 text-dark fs-6">
                            {service.name}
                          </h6>
                          {isRetailPriceVisible && (
                            <small className="text-muted fw-normal d-block">
                              {isCustom
                                ? "Custom"
                                : `${formatCurrency(
                                    service.unit_price,
                                    quotationDetails?.currency,
                                  )} / ${displayPriceType}`}
                            </small>
                          )}
                          {service.is_date_required && (
                            <small className="text-muted fw-normal d-block">
                              <>
                                {service?.date_label
                                  ? service?.date_label
                                  : "Installation Date"}
                                :{" "}
                                {service?.installation_date
                                  ? formatToLocalDate(service.installation_date)
                                  : "No Preference"}
                              </>
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-12">
                      <div className="row justify-content-md-end justify-content-between align-items-center gx-2">
                        <div className="col-3">
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            {showPerson && (
                              <>
                                <small className="text-muted fw-normal text-nowrap">
                                  Person:
                                </small>
                                <span className="fw-semibold text-dark">
                                  {service.quantity}
                                </span>
                              </>
                            )}
                            {showQty && (
                              <>
                                <small className="text-muted fw-normal text-nowrap">
                                  Qty:
                                </small>
                                <span className="fw-semibold text-dark">
                                  {service.quantity}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            {showDays && (
                              <>
                                <small className="text-muted fw-normal text-nowrap">
                                  Days:
                                </small>
                                <span className="fw-semibold text-dark">
                                  {service.days}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {isRetailPriceVisible && (
                          <div className="col-4">
                            <div className="d-flex justify-content-end align-items-start gap-1">
                              <div className="d-flex align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap">
                                  Total:
                                </small>
                                <span className="fw-bold text-dark text-nowrap">
                                  {formatCurrency(
                                    service.line_total,
                                    quotationDetails?.currency,
                                  )}
                                </span>
                              </div>
                              {showHiddenRetailPriceTooltip && (
                                <Tooltip
                                  title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}
                                >
                                  <span className="svg-16 text-muted mt-1 cursor-pointer d-inline-flex align-items-center">
                                    {svg?.restrictedAvatarIcon}
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center text-muted py-3">No services added.</div>
        )}
        {serviceList.length > 0 && (
          <div className="text-end fs-5 mt-2">
            Services Total Amount:{" "}
            <span className="fw-bold">
              {formatCurrency(data.services_total, quotationDetails?.currency)}
            </span>
          </div>
        )}
      </div>

      {/* Installation Checklist */}
      {/* {isInstallationRequired && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">
              Installation Checklist
            </h4>
            {!readOnly && (
              <span
                className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
                onClick={() =>
                  goToStep(CreateQuotationStepsEnum.INSTALLATION_CHECKLIST)
                }
              >
                Modify
              </span>
            )}
          </div>
          <div className="d-flex flex-column gap-3 mb-4">
            {checklist.length > 0 ? (
              checklist.map((item: any) => (
                <div
                  key={item.question_id}
                  className="card border-0 shadow-sm rounded-4 bg-white"
                >
                  <div className="card-body p-md-4 p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2 mb-2">
                      <div
                        className="fw-normal text-dark fs-6"
                        style={{ flex: 1 }}
                      >
                        {item.question_text}
                      </div>
                      <div className="text-949494 fs-7 text-md-end text-start">
                        {getFilledByText(item)}
                      </div>
                    </div>
                    <div className="mt-1">{renderChecklistAnswer(item)}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white text-center text-muted">
                No checklist answers recorded.
              </div>
            )}
          </div>
        </>
      )} */}
      {/* Delivery */}
      {(deliveryDetails?.name ||
        deliveryDetails?.on_site_delivery_date ||
        deliveryDetails?.delivery_notes ||
        !(
          mezzanineDeliveryDetails?.name ||
          mezzanineDeliveryDetails?.on_site_delivery_date ||
          mezzanineDeliveryDetails?.delivery_notes
        )) && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">Delivery</h4>
            {!readOnly && (
              <span
                className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
                onClick={() =>
                  goToStep(CreateQuotationStepsEnum.COLLECTION_DELIVERY)
                }
              >
                Modify
              </span>
            )}
          </div>
          <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4">
            {deliveryDetails?.name ||
            deliveryDetails?.on_site_delivery_date ||
            deliveryDetails?.delivery_notes ? (
              <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                <div className="row align-items-center gy-2">
                  {deliveryDetails?.name && (
                    <>
                      {isCourierDelivery ? (
                        <>
                          {/* Title Row: Delivery Name on Left, Delivery Total on Right */}
                          <div className="col-md-6 col-12">
                            <h6 className="fw-bold mb-0 text-dark fs-6">
                              {deliveryDetails.name}
                            </h6>
                          </div>
                          <div className="col-md-6 col-12">
                            <div className="d-flex justify-content-md-end justify-content-between align-items-center">
                              <div
                                style={{ minWidth: "110px" }}
                                className="d-flex align-items-center gap-2"
                              >
                                <small className="text-muted fw-normal text-nowrap">
                                  Total:
                                </small>
                                <span className="fw-bold text-dark text-nowrap">
                                  {formatCurrency(
                                    deliveryDetails.total ??
                                      deliveryDetails.price,
                                    quotationDetails?.currency,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Multiple Rows: Each Parcel Box */}
                          {parcelBoxes.length > 0 ? (
                            parcelBoxes.map((box: any, idx: number) => {
                              const unitPrice = Number(
                                box.price ?? box.unit_price ?? 0,
                              );
                              const qty = Math.max(
                                1,
                                Number(box.quantity) || 1,
                              );
                              const lineTotal = Number(
                                box.line_total ?? box.total ?? unitPrice * qty,
                              );
                              const dimensions = box.dimensions_text;

                              return (
                                <React.Fragment
                                  key={box.box_id || box._id || idx}
                                >
                                  {/* Left Side: Parcel Box Name */}
                                  <div className="col-md-6 col-12">
                                    <div
                                      className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                      style={{ fontSize: "0.875rem" }}
                                    >
                                      <span
                                        style={{
                                          color:
                                            DeliveryLocationQtyTypeColors[
                                              DeliveryLocationQtyType.PARCEL_BOX
                                            ] || "#0d6efd",
                                        }}
                                      >
                                        {box.name} {dimensions}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Right Side: Price, Quantity & Line Total */}
                                  <div className="col-md-6 col-12">
                                    <div className="d-flex justify-content-md-end justify-content-between align-items-center gap-3 flex-wrap">
                                      <div
                                        style={{ minWidth: "125px" }}
                                        className="d-flex align-items-center gap-2"
                                      >
                                        <small className="text-muted fw-normal text-nowrap">
                                          Price:
                                        </small>
                                        <span className="fw-bold text-dark text-nowrap">
                                          {formatCurrency(
                                            unitPrice,
                                            quotationDetails?.currency,
                                          )}
                                        </span>
                                      </div>
                                      <div
                                        style={{ minWidth: "60px" }}
                                        className="d-flex align-items-center gap-2"
                                      >
                                        <small className="text-muted fw-normal text-nowrap text-capitalize">
                                          Qty:
                                        </small>
                                        <span className="fw-bold text-dark">
                                          {qty}
                                        </span>
                                      </div>
                                      <div
                                        style={{ minWidth: "110px" }}
                                        className="d-flex align-items-center gap-2"
                                      >
                                        <small className="text-muted fw-normal text-nowrap">
                                          Total:
                                        </small>
                                        <span className="fw-bold text-dark text-nowrap">
                                          {formatCurrency(
                                            lineTotal,
                                            quotationDetails?.currency,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <>
                              <div className="col-md-6 col-12">
                                <div
                                  className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  <span
                                    style={{
                                      color:
                                        DeliveryLocationQtyTypeColors[
                                          DeliveryLocationQtyType.PARCEL_BOX
                                        ] || "#0d6efd",
                                    }}
                                  >
                                    Parcel Delivery
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6 col-12">
                                <div className="d-flex justify-content-md-end justify-content-between align-items-center gap-3 flex-wrap">
                                  <div
                                    style={{ minWidth: "125px" }}
                                    className="d-flex align-items-center gap-2"
                                  >
                                    <small className="text-muted fw-normal text-nowrap">
                                      Price:
                                    </small>
                                    <span className="fw-bold text-dark text-nowrap">
                                      {getDeliveryPriceLabel(
                                        deliveryDetails.price,
                                        quotationDetails?.currency,
                                        quotationDetails?.conversion_rate,
                                      )}
                                    </span>
                                  </div>
                                  <div
                                    style={{ minWidth: "60px" }}
                                    className="d-flex align-items-center gap-2"
                                  >
                                    <small className="text-muted fw-normal text-nowrap text-capitalize">
                                      Qty:
                                    </small>
                                    <span className="fw-bold text-dark">
                                      {deliveryDetails.quantity || 1}
                                    </span>
                                  </div>
                                  <div
                                    style={{ minWidth: "110px" }}
                                    className="d-flex align-items-center gap-2"
                                  >
                                    <small className="text-muted fw-normal text-nowrap">
                                      Total:
                                    </small>
                                    <span className="fw-bold text-dark text-nowrap">
                                      {formatCurrency(
                                        deliveryDetails.total ??
                                          deliveryDetails.price,
                                        quotationDetails?.currency,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div
                            className={
                              deliveryDetails?.type ===
                              DeliveryLocationType.FREE
                                ? "col-12"
                                : "col-md-6 col-12"
                            }
                          >
                            <div className="d-flex align-items-center gap-2">
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="fw-bold mb-0 text-dark fs-6">
                                  {deliveryDetails?.name}
                                </h6>
                                {deliveryDetails?.type !==
                                  DeliveryLocationType.FREE &&
                                  (deliveryDetails?.qty_type ===
                                  DeliveryLocationQtyType.TRAILERS ? (
                                    <div className="d-flex gap-sm-4 gap-3">
                                      <div
                                        className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 fw-normal text-secondary"
                                        style={{ fontSize: "0.875rem" }}
                                      >
                                        <span
                                          className="text-nowrap"
                                          style={{
                                            color:
                                              DeliveryLocationQtyTypeColors[
                                                deliveryDetails.qty_type as DeliveryLocationQtyType
                                              ],
                                          }}
                                        >
                                          Half Trailer
                                        </span>
                                        <span className="mx-1 d-none d-sm-inline">
                                          |
                                        </span>
                                        <span>
                                          {getDeliveryPriceLabel(
                                            deliveryDetails.half_trailer_price,
                                            quotationDetails?.currency,
                                            quotationDetails?.conversion_rate,
                                          )}
                                        </span>
                                      </div>
                                      <div
                                        className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 fw-normal text-secondary"
                                        style={{ fontSize: "0.875rem" }}
                                      >
                                        <span
                                          className="text-nowrap"
                                          style={{
                                            color:
                                              DeliveryLocationQtyTypeColors[
                                                deliveryDetails.qty_type as DeliveryLocationQtyType
                                              ],
                                          }}
                                        >
                                          Full Trailer
                                        </span>
                                        <span className="mx-1 d-none d-sm-inline">
                                          |
                                        </span>
                                        <span>
                                          {getDeliveryPriceLabel(
                                            deliveryDetails.price,
                                            quotationDetails?.currency,
                                            quotationDetails?.conversion_rate,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                      style={{ fontSize: "0.875rem" }}
                                    >
                                      <span
                                        style={{
                                          color:
                                            DeliveryLocationQtyTypeColors[
                                              deliveryDetails.qty_type as DeliveryLocationQtyType
                                            ],
                                        }}
                                      >
                                        {getDeliveryQtyTypeLabel(
                                          deliveryDetails.qty_type,
                                        )}
                                      </span>
                                      <span className="mx-1">|</span>
                                      <span>
                                        {getDeliveryPriceLabel(
                                          deliveryDetails.price,
                                          quotationDetails?.currency,
                                          quotationDetails?.conversion_rate,
                                        )}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                          {deliveryDetails?.type !==
                            DeliveryLocationType.FREE && (
                            <div className="col-md-6 col-12">
                              <div className="row justify-content-md-end justify-content-between align-items-center gx-3 gy-1">
                                <div className="col-auto">
                                  <div className="d-flex justify-content-end align-items-center gap-2">
                                    <small className="text-muted fw-normal text-nowrap text-capitalize">
                                      {getDeliveryQtyTypeLabel(
                                        deliveryDetails.qty_type,
                                      )}{" "}
                                      Qty:
                                    </small>
                                    <span className="fw-semibold text-dark">
                                      {deliveryDetails.quantity}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className="col-auto"
                                  style={{ minWidth: "130px" }}
                                >
                                  <div className="d-flex justify-content-end align-items-center gap-2">
                                    <small className="text-muted fw-normal text-nowrap">
                                      Total:
                                    </small>
                                    <span className="fw-bold text-dark text-nowrap">
                                      {formatCurrency(
                                        deliveryDetails.total,
                                        quotationDetails?.currency,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {deliveryDetails?.on_site_delivery_date && (
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2">
                        <small className="fs-6 fw-normal text-949494 d-block">
                          On-site Delivery Date (Not Guaranteed):
                        </small>
                        <span className="fw-medium text-dark text-nowrap">
                          {formatToLocalDate(
                            deliveryDetails.on_site_delivery_date,
                            true,
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {deliveryDetails?.delivery_notes && (
                    <div className="d-flex align-items-start gap-2">
                      <small className="fs-6 fw-normal text-949494 d-block flex-shrink-0">
                        Delivery Notes:
                      </small>
                      <span className="fw-medium text-dark text-break">
                        {deliveryDetails.delivery_notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card-body py-3 px-3">
                <div className="text-center text-muted py-3">
                  No delivery selected.
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {machineDeliveryDetails?.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">Machine Delivery</h4>
          </div>
          {machineDeliveryDetails?.length > 0 ? (
            machineDeliveryDetails?.map((item: any, index: number) => {
              return (
                <div
                  key={item.name}
                  className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4"
                >
                  <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                    <div className="row gy-2 align-items-center">
                      <div className="col-md-6 col-12">
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex flex-column justify-content-center">
                            <h6 className="fw-bold mb-0 text-dark fs-6">
                              {item?.name}
                            </h6>
                            <div
                              className="d-flex align-items-center gap-1 fw-normal text-secondary"
                              style={{ fontSize: "0.875rem" }}
                            >
                              <span>
                                {formatCurrency(
                                  item?.price,
                                  quotationDetails?.currency,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-12">
                        <>
                          <div className="row justify-content-md-end justify-content-between align-items-center gx-2">
                            <div className="col-4">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap text-capitalize">
                                  {item?.quantity_name
                                    ? item?.quantity_name
                                    : "Qty"}
                                  :
                                </small>
                                <span className="fw-semibold text-dark">
                                  {item?.quantity ?? "-"}
                                </span>
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap">
                                  Total:
                                </small>
                                <span className="fw-bold text-dark text-nowrap">
                                  {formatCurrency(
                                    item?.total ?? 0,
                                    quotationDetails?.currency,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4">
              <div className="text-center text-muted py-3">
                No machine delivery selected.
              </div>
            </div>
          )}
        </>
      )}
      {mezzanineDeliveryDetails && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">
              Mezzanine Delivery
            </h4>
            {!readOnly && (
              <span
                className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
                onClick={() =>
                  goToStep(CreateQuotationStepsEnum.COLLECTION_DELIVERY)
                }
              >
                Modify
              </span>
            )}
          </div>
          <div className="card d-flex flex-column gap-2 border-0 shadow-sm rounded-4 bg-white mb-4">
            {mezzanineDeliveryDetails?.name ||
            mezzanineDeliveryDetails?.on_site_delivery_date ||
            mezzanineDeliveryDetails?.delivery_notes ? (
              <div className="card-body py-3 px-3 fs-6 fw-semibold text-dark">
                <div className="row align-items-center gy-2">
                  {mezzanineDeliveryDetails?.name && (
                    <>
                      <div
                        className={
                          mezzanineDeliveryDetails?.type ===
                          DeliveryLocationType.FREE
                            ? "col-12"
                            : "col-md-6 col-12"
                        }
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div className="d-flex flex-column justify-content-center">
                            <h6 className="fw-bold mb-0 text-dark fs-6">
                              {mezzanineDeliveryDetails?.name}
                            </h6>
                            {mezzanineDeliveryDetails?.type !==
                              DeliveryLocationType.FREE &&
                              (mezzanineDeliveryDetails?.qty_type ===
                              DeliveryLocationQtyType.TRAILERS ? (
                                <div className="d-flex gap-sm-4 gap-3">
                                  {mezzanineDeliveryDetails?.half_trailer_price !==
                                    undefined &&
                                    mezzanineDeliveryDetails?.half_trailer_price !==
                                      null && (
                                      <div
                                        className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 fw-normal text-secondary"
                                        style={{ fontSize: "0.875rem" }}
                                      >
                                        <span
                                          className="text-nowrap"
                                          style={{
                                            color:
                                              DeliveryLocationQtyTypeColors[
                                                mezzanineDeliveryDetails.qty_type as DeliveryLocationQtyType
                                              ],
                                          }}
                                        >
                                          Half Trailer
                                        </span>
                                        <span className="mx-1 d-none d-sm-inline">
                                          |
                                        </span>
                                        <span>
                                          {getDeliveryPriceLabel(
                                            mezzanineDeliveryDetails.half_trailer_price,
                                            quotationDetails?.currency,
                                            quotationDetails?.conversion_rate,
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  <div
                                    className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 fw-normal text-secondary"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    <span
                                      className="text-nowrap"
                                      style={{
                                        color:
                                          DeliveryLocationQtyTypeColors[
                                            mezzanineDeliveryDetails.qty_type as DeliveryLocationQtyType
                                          ],
                                      }}
                                    >
                                      Full Trailer
                                    </span>
                                    <span className="mx-1 d-none d-sm-inline">
                                      |
                                    </span>
                                    <span>
                                      {getDeliveryPriceLabel(
                                        mezzanineDeliveryDetails.price,
                                        quotationDetails?.currency,
                                        quotationDetails?.conversion_rate,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="d-flex align-items-center gap-1 fw-normal text-secondary"
                                  style={{ fontSize: "0.875rem" }}
                                >
                                  <span
                                    style={{
                                      color:
                                        DeliveryLocationQtyTypeColors[
                                          mezzanineDeliveryDetails.qty_type as DeliveryLocationQtyType
                                        ],
                                    }}
                                  >
                                    {getDeliveryQtyTypeLabel(
                                      mezzanineDeliveryDetails.qty_type,
                                    )}
                                  </span>
                                  <span className="mx-1">|</span>
                                  <span>
                                    {getDeliveryPriceLabel(
                                      mezzanineDeliveryDetails.price,
                                      quotationDetails?.currency,
                                      quotationDetails?.conversion_rate,
                                    )}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>

                      {mezzanineDeliveryDetails?.type !==
                        DeliveryLocationType.FREE && (
                        <div className="col-md-6 col-12">
                          <div className="row justify-content-md-end justify-content-between align-items-center gx-3 gy-1">
                            <div className="col-auto">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap text-capitalize">
                                  {getDeliveryQtyTypeLabel(
                                    mezzanineDeliveryDetails.qty_type,
                                  )}{" "}
                                  Qty:
                                </small>
                                <span className="fw-semibold text-dark">
                                  {mezzanineDeliveryDetails.quantity}
                                </span>
                              </div>
                            </div>
                            <div className="col-auto">
                              <div className="d-flex justify-content-end align-items-center gap-2">
                                <small className="text-muted fw-normal text-nowrap">
                                  Total:
                                </small>
                                <span className="fw-bold text-dark text-nowrap">
                                  {formatCurrency(
                                    mezzanineDeliveryDetails.total,
                                    quotationDetails?.currency,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {mezzanineDeliveryDetails?.on_site_delivery_date && (
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-2">
                        <small className="fs-6 fw-normal text-949494 d-block">
                          On-site Delivery Date (Not Guaranteed):
                        </small>
                        <span className="fw-medium text-dark text-nowrap">
                          {formatToLocalDate(
                            mezzanineDeliveryDetails.on_site_delivery_date,
                            true,
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {mezzanineDeliveryDetails?.delivery_notes && (
                    <div className="d-flex align-items-start gap-2">
                      <small className="fs-6 fw-normal text-949494 d-block flex-shrink-0">
                        Delivery Notes:
                      </small>
                      <span className="fw-medium text-dark text-break">
                        {mezzanineDeliveryDetails.delivery_notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-muted py-3">
                No mezzanine delivery selected.
              </div>
            )}
          </div>
        </>
      )}
      {/* Notes */}
      {!isCustomerLoggedIn() && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">Internal Notes</h4>
            {!readOnly && (
              <span
                className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
                onClick={() => goToStep(CreateQuotationStepsEnum.NOTES)}
              >
                Modify
              </span>
            )}
          </div>
          <div className="card border-0 shadow-sm p-3 rounded-4 bg-white mb-4">
            {internalNotes.length > 0 ? (
              internalNotes.map(renderNoteItem)
            ) : (
              <div className="text-center text-muted py-3">
                No internal notes added.
              </div>
            )}
          </div>
        </>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Client Notes</h4>
        {!readOnly && (
          <span
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={() => goToStep(CreateQuotationStepsEnum.NOTES)}
          >
            Modify
          </span>
        )}
      </div>
      <div className="card border-0 shadow-sm p-3 rounded-4 bg-white mb-4">
        {clientNotes.length > 0 ? (
          clientNotes.map(renderNoteItem)
        ) : (
          <div className="text-center text-muted py-3">
            No client notes added.
          </div>
        )}
      </div>
      {/* Advertisement Sign */}
      {Number(quotationDetails?.advertisement_sign) > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fs-4 fw-bold text-primary mb-0">
              Advertisement Sign
            </h4>
            {!readOnly && (
              <span
                className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
                onClick={() => goToStep(CreateQuotationStepsEnum.LOAD_NOTICES)}
              >
                Modify
              </span>
            )}
          </div>
          <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
            <div className="card-body py-3 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h6 className="fw-bold mb-0 text-dark fs-6">
                Advertisement Sign
              </h6>
              <div className="fs-6 text-secondary fw-normal">
                Qty:{" "}
                <span className="fw-bold text-dark">
                  {quotationDetails.advertisement_sign}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Designs */}
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fs-4 fw-bold text-primary mb-0">Designs</h4>
          {!readOnly && (
            <span
              className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
              onClick={() => goToStep(CreateQuotationStepsEnum.DESIGNS)}
            >
              Modify
            </span>
          )}
        </div>
        <div className="card d-flex flex-column gap-2 border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
          <div className="d-flex align-items-center gap-5 mb-3 border-bottom pb-2">
            <div>
              <label className="fs-6 fw-normal text-949494">
                Drawing Number:
              </label>{" "}
              <span className="fw-semibold text-primary fs-6">
                {data.design_drawing_number || "-"}
              </span>
            </div>
            <div>
              <label className="fs-6 fw-normal text-949494">Revision:</label>{" "}
              <span className="fw-semibold text-primary fs-6">
                {data.design_revision || "-"}
              </span>
            </div>
          </div>
          {designList.length > 0 ? (
            designList.map((design: any, idx: number) => (
              <div key={design._id || idx} className="card border rounded-3">
                <div className="card-body py-2 px-3 d-flex align-items-center justify-content-between gap-2 flex-wra p flex-md-row flex-column">
                  <div className="fs-5 fw-semibold text-break">
                    {getCleanFileName(design.file_url)}
                  </div>
                  <span
                    className="fs-5 fw-bold text-primary text-decoration-underline cursor-pointer"
                    onClick={() => {
                      const urls = designList.map((d: any) => d.file_url);
                      setPreviewFiles(urls);
                      setPreviewIndex(idx);
                      setPreviewModalOpen(true);
                    }}
                  >
                    View
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-muted text-center py-3">
              No designs uploaded.
            </div>
          )}
        </div>
      </>
      {/* Grand Totals */}
      <div className="row justify-content-end mt-5 pe-2">
        <div className="col-md-6">
          {quotationDetails?.currency &&
          quotationDetails?.currency !== "GBP" ? (
            <>
              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-6 text-muted gap-2 mb-1">
                <span>Base Subtotal (GBP): </span>
                <span className="fw-semibold text-nowrap">
                  {formatCurrency(data.subtotal, "GBP", 1)}
                </span>
              </div>
              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2 mb-3">
                <span className="text-dark">
                  Subtotal ({quotationDetails.currency}):{" "}
                </span>
                <span className="fw-bold text-dark text-nowrap">
                  {formatCurrency(
                    data.subtotal,
                    quotationDetails.currency,
                    quotationDetails?.conversion_rate,
                  )}
                </span>
              </div>

              {data?.vat_percentage !== undefined &&
                data?.vat_percentage !== null &&
                Number(data?.vat_percentage) > 0 &&
                Number(data?.vat_amount) > 0 && (
                  <>
                    <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-6 text-muted gap-2 mb-1">
                      <span>Base VAT ({data.vat_percentage}% GBP): </span>
                      <span className="fw-semibold text-nowrap">
                        {formatCurrency(data.vat_amount, "GBP", 1)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2 mb-3">
                      <span className="text-dark">
                        VAT ({data?.vat_percentage}% {quotationDetails.currency}
                        ):
                      </span>
                      <span className="fw-bold text-dark text-nowrap">
                        {formatCurrency(
                          data.vat_amount,
                          quotationDetails.currency,
                          quotationDetails?.conversion_rate,
                        )}
                      </span>
                    </div>
                  </>
                )}

              <div
                className={`d-column d-lg-flex ${
                  readOnly ? "justify-content-end" : "justify-content-between"
                }  align-items-center fs-6 gap-2 mb-2`}
              >
                {readOnly || isCustomerLoggedIn() ? (
                  ""
                ) : (
                  <div className="d-flex justify-content-between justify-content-md-end justify-content-lg-start  align-items-center gap-2 mb-2 mb-lg-0">
                    <span className="text-secondary fw-medium">
                      Markup / Discount :{" "}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-light text-dark border fw-bold text-nowrap px-2 py-1">
                        {Number(
                          quotationDetails?.margin ??
                            quotationDetails?.customer_snapshot?.margin,
                        ) > 0 ? (
                          <span className="text-success d-inline-flex align-items-center gap-1">
                            <span>Markup :</span>
                            <span className="fw-bold">
                              {quotationDetails?.margin ??
                                quotationDetails?.customer_snapshot?.margin}
                              %
                            </span>
                          </span>
                        ) : Number(
                            quotationDetails?.customer_snapshot?.discount ??
                              quotationDetails?.discount,
                          ) > 0 ? (
                          <span className="text-danger d-inline-flex align-items-center gap-1">
                            <span>Discount :</span>
                            <span className="fw-bold">
                              {quotationDetails?.customer_snapshot?.discount ??
                                quotationDetails?.discount}
                              %
                            </span>
                          </span>
                        ) : (
                          <span className="text-muted">0%</span>
                        )}
                      </span>
                      {!readOnly && mode !== "view" && (
                        <Tooltip title={`Edit Markup or Discount`}>
                          <div
                            className="cursor-pointer d-flex align-items-center justify-content-center svg-20"
                            onClick={() => setIsMarginDiscountModalOpen(true)}
                          >
                            {svg?.editBtn}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )}
                {data.currency !== "GBP" && (
                  <div className="d-flex justify-content-between justify-content-md-end justify-content-lg-end align-items-center gap-2">
                    <span className="text-secondary fw-medium">
                      Conversion Rate:{" "}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-light text-dark border fw-bold text-nowrap px-2 py-1">
                        1 GBP = {quotationDetails?.conversion_rate || 1}{" "}
                        {quotationDetails.currency}
                      </span>
                      {mode !== "view" &&
                        isExchangeManual &&
                        quotationDetails.currency &&
                        quotationDetails.currency !== "GBP" &&
                        ALLOWED_ROLES_FOR_EXCHANGE_RATE.includes(
                          getUserRole() as string,
                        ) && (
                          <Tooltip
                            title={`Edit Exchange Rate for ${quotationDetails.currency}`}
                          >
                            <div
                              className="cursor-pointer d-flex align-items-center justify-content-center svg-20"
                              onClick={() => {
                                setCustomRateInput(
                                  quotationDetails.conversion_rate || 1,
                                );
                                setShowCustomRateModal(true);
                              }}
                            >
                              {svg?.editBtn}
                            </div>
                          </Tooltip>
                        )}
                    </div>
                  </div>
                )}
              </div>

              <Divider className="my-3 border-dark" />

              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-6 text-muted gap-2 mb-1">
                <span>Base Grand Total (GBP): </span>
                <span className="fw-semibold text-nowrap">
                  {formatCurrency(data.grand_total, "GBP", 1)}
                </span>
              </div>
              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2">
                <h5 className="text-dark mb-0 fs-5 fs-6-mob">
                  Grand Total ({quotationDetails.currency}):
                </h5>
                <h5 className="fw-bold text-dark mb-0 fs-5 fs-6-mob text-nowrap">
                  {formatCurrency(
                    data.grand_total,
                    quotationDetails.currency,
                    quotationDetails?.conversion_rate,
                  )}
                </h5>
              </div>
            </>
          ) : (
            <>
              {readOnly || isCustomerLoggedIn() ? (
                ""
              ) : (
                <div className="d-flex justify-content-between justify-content-md-end align-items-center gap-2 mb-2">
                  <span className="text-secondary fw-medium">
                    Markup / Discount :{" "}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border fw-bold text-nowrap px-2 py-1">
                      {Number(
                        quotationDetails?.margin ??
                          quotationDetails?.customer_snapshot?.margin,
                      ) > 0 ? (
                        <span className="text-success d-inline-flex align-items-center gap-1">
                          <span>Markup :</span>
                          <span className="fw-bold">
                            {quotationDetails?.margin ??
                              quotationDetails?.customer_snapshot?.margin}
                            %
                          </span>
                        </span>
                      ) : Number(
                          quotationDetails?.customer_snapshot?.discount ??
                            quotationDetails?.discount,
                        ) > 0 ? (
                        <span className="text-danger d-inline-flex align-items-center gap-1">
                          <span>Discount :</span>
                          <span className="fw-bold">
                            {quotationDetails?.customer_snapshot?.discount ??
                              quotationDetails?.discount}
                            %
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted">0%</span>
                      )}
                    </span>
                    {!readOnly && mode !== "view" && (
                      <Tooltip title={`Edit Markup or Discount`}>
                        <div
                          className="cursor-pointer d-flex align-items-center justify-content-center svg-20"
                          onClick={() => setIsMarginDiscountModalOpen(true)}
                        >
                          {svg?.editBtn}
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2 mb-3">
                <span className="text-dark">Subtotal: </span>
                <span className="fw-bold text-dark text-nowrap">
                  {formatCurrency(data.subtotal, quotationDetails?.currency)}
                </span>
              </div>
              {data?.vat_percentage !== undefined &&
                data?.vat_percentage !== null &&
                Number(data?.vat_percentage) > 0 &&
                Number(data?.vat_amount) > 0 && (
                  <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2 mb-3">
                    <span className="text-dark">
                      VAT ({data?.vat_percentage}%):
                    </span>
                    <span className="fw-bold text-dark text-nowrap">
                      {formatCurrency(
                        data.vat_amount,
                        quotationDetails?.currency,
                      )}
                    </span>
                  </div>
                )}
              <Divider className="my-3 border-dark" />
              <div className="d-flex justify-content-between justify-content-md-end align-items-center fs-5 fs-6-mob gap-2">
                <h5 className="text-dark mb-0 fs-5 fs-6-mob">Grand Total:</h5>
                <h5 className="fw-bold text-dark mb-0 fs-5 fs-6-mob text-nowrap">
                  {formatCurrency(data.grand_total, quotationDetails?.currency)}
                </h5>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Actions */}
      {!readOnly && (
        <div className="row justify-content-center gy-2 mt-4">
          <div className="col-md-auto col-sm-4 col-6">
            <Button
              className="btn btn-lg btn-outline-secondary w-100 px-4 h-auto text-uppercase"
              disabled={!!loadingAction}
              onClick={() => {
                dispatch(resetQuotationState());
                navigate("/quotations");
              }}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-auto col-sm-4 col-6">
            <Button
              className="btn btn-lg btn-outline-primary w-100 px-4 h-auto text-uppercase"
              disabled={!!loadingAction}
              onClick={() => {
                const nextParams: any = {
                  step: CreateQuotationStepsEnum.DESIGNS,
                  quotationId: quotationId,
                };
                setSearchParams(nextParams);
              }}
            >
              Previous
            </Button>
          </div>
          {[
            QuoteStatus.DRAFT,
            QuoteStatus.DESIGN_PENDING,
            QuoteStatus.DESIGN_APPROVED,
          ].includes(quotationDetails.status) && (
            <div className="col-md-auto col-sm-4">
              <Button
                className="btn btn-lg btn-outline-info w-100 px-4 h-auto text-uppercase"
                disabled={!!loadingAction}
                onClick={() => {
                  dispatch(resetQuotationState());
                  navigate("/quotations");
                }}
              >
                Save As Draft
              </Button>
            </div>
          )}
          {!isCustomerLoggedIn() && (
            <>
              {isAdminSubAdminLoggedIn() &&
                isAdminApprovalOn &&
                [
                  QuoteStatus.DRAFT,
                  QuoteStatus.PENDING_ADMIN_APPROVAL,
                ].includes(quotationDetails.status) && (
                  <div className="col-md-auto">
                    <Button
                      className="btn btn-lg btn-primary w-100 px-4 h-auto text-uppercase"
                      loading={loadingAction === "admin_approved"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                      onClick={() => changeStatusHandler("admin_approved")}
                    >
                      Mark Admin Approved
                    </Button>
                  </div>
                )}
              {(isAdminSubAdminLoggedIn() || !isAdminApprovalOn
                ? quotationDetails.is_design === false ||
                  ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                    quotationDetails.status,
                  )
                : (quotationDetails.is_design === false ||
                    ![QuoteStatus.DRAFT, QuoteStatus.DESIGN_PENDING].includes(
                      quotationDetails.status,
                    )) &&
                  ![
                    QuoteStatus.DRAFT,
                    QuoteStatus.DESIGN_APPROVED,
                    QuoteStatus.PENDING_ADMIN_APPROVAL,
                  ].includes(quotationDetails.status)) && (
                <>
                  {/* <div className="col-md-auto col-sm-4">
                    <Button
                      className="btn btn-lg btn-info w-100 px-4 h-auto text-uppercase"
                      onClick={() => changeStatusHandler("saved")}
                      loading={loadingAction === "saved"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                      style={{ minWidth: "8.438rem" }}
                    >
                      Save
                    </Button>
                  </div> */}
                  <div className="col-md-auto">
                    <Button
                      className="btn btn-lg btn-primary w-100 px-4 h-auto text-uppercase"
                      onClick={() => changeStatusHandler("sent")}
                      loading={loadingAction === "sent"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                    >
                      Email & Mark Sent
                    </Button>
                  </div>
                </>
              )}

              {!isAdminSubAdminLoggedIn() &&
                isAdminApprovalOn &&
                (quotationDetails.status ===
                  QuoteStatus.PENDING_ADMIN_APPROVAL ||
                  (quotationDetails.is_design &&
                    quotationDetails.status === QuoteStatus.DESIGN_APPROVED) ||
                  (!quotationDetails.is_design &&
                    quotationDetails.status === QuoteStatus.DRAFT)) && (
                  <div className="col-md-auto">
                    <Button
                      className="btn btn-lg btn-primary w-100 px-4 h-auto text-uppercase"
                      loading={loadingAction === "pending_admin_approval"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                      onClick={() =>
                        changeStatusHandler("pending_admin_approval")
                      }
                    >
                      Send For Approval
                    </Button>
                  </div>
                )}

              {[QuoteStatus.DRAFT].includes(quotationDetails.status) &&
                quotationDetails.is_design && (
                  <div className="col-md-auto">
                    <Button
                      className="btn btn-lg btn-primary w-100 px-4 h-auto text-uppercase"
                      loading={loadingAction === "design_pending"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                      onClick={() => changeStatusHandler("design_pending")}
                    >
                      Send To Design Team
                    </Button>
                  </div>
                )}
              {[QuoteStatus.DESIGN_PENDING].includes(quotationDetails.status) &&
                [
                  RoleTypesEnum.ADMIN,
                  RoleTypesEnum.SUB_ADMIN,
                  RoleTypesEnum.DESIGN_TEAM,
                ].includes(getUserRole() as RoleTypesEnum) &&
                quotationDetails.is_design && (
                  <div className="col-md-auto">
                    <Button
                      className="btn btn-lg btn-primary w-100 px-4 h-auto text-uppercase"
                      loading={loadingAction === "design_approved"} // ✅ Specific Loader
                      disabled={!!loadingAction}
                      onClick={() => changeStatusHandler("design_approved")}
                    >
                      Mark Design Approved
                    </Button>
                  </div>
                )}
            </>
          )}
        </div>
      )}
      {/* Sticky Reverse Calculation Discrepancy Bar */}
      {isReverseCalculationUnbalanced && (
        <>
          <div style={{ height: "48px" }} />
          <div className="quote-review-sticky-mismatch-bar py-2 px-3">
            <div className="mismatch-ticker-track">
              <div className="mismatch-ticker-item">
                <span
                  style={{
                    color: "#344054",
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  <span style={{ fontWeight: 500, marginRight: "4px" }}>
                    Total Mismatch Detected:
                  </span>
                  The difference between the quotation's total value and the combined value of all line items is{" "}
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#000000",
                    }}
                  >
                    {formattedDiscrepancyAmount}
                  </span>{". "}
                  Please review the line items and calculations.
                </span>
              </div>
              <div className="mismatch-ticker-item duplicate">
                <span
                  style={{
                    color: "#344054",
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  <span style={{ fontWeight: 500, marginRight: "4px" }}>
                    Total Mismatch Detected:
                  </span>
                  The difference between the quotation's total value and the combined value of all line items is{" "}
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#000000",
                    }}
                  >
                    {formattedDiscrepancyAmount}
                  </span>{". "}
                  Please review the line items and calculations.
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
      <NoItemsAddedModal
        open={isNoItemsModalOpen}
        onClose={() => {
          setIsNoItemsModalOpen(false);
        }}
        showServiceAction={true}
      />
      <WarningsModal
        warnings={apiWarnings}
        onClose={() => {
          setApiWarnings([]);
          setApiWarningSeverity("");
          setPendingStatus(null);
        }}
        actions={
          apiWarningSeverity === WarningSeverity.BLOCKING
            ? WarningActionLayouts.Blocking(() => {
                setApiWarnings([]);
                setApiWarningSeverity("");
                setPendingStatus(null);
              })
            : WarningActionLayouts.BypassableSave(
                () => {
                  setApiWarnings([]);
                  setApiWarningSeverity("");
                  setPendingStatus(null);
                },
                () => {
                  if (pendingStatus) {
                    changeStatusHandler(pendingStatus as any, true);
                  }
                },
                !!loadingAction,
              )
        }
      />
      <CommonConfirmationModal
        open={showEmailSentConfirmModal}
        onClose={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          setShowEmailSentConfirmModal(false);
        }}
        onSuccess={() => {
          if (mailerTimeoutRef.current) clearTimeout(mailerTimeoutRef.current);
          setShowEmailSentConfirmModal(false);
          executeStatusUpdate("sent", pendingSentForceUpdate);
        }}
        title="Has the Email Been Sent?"
        subTitle="Please confirm once you have sent the email to the client using your default mail app."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Yes, Email Sent"
        isContantLoading={isContantLoading}
      />
      <DownloadMailerAppModal
        open={showDesktopAppError}
        onClose={() => setShowDesktopAppError(false)}
      />
      <DesktopUpdateModal
        visible={showDesktopUpdateModal}
        onClose={() => {
          setShowDesktopUpdateModal(false);
          pendingSendMailRef.current = null;
        }}
        onProceed={() => {
          if (pendingSendMailRef.current) {
            pendingSendMailRef.current();
            pendingSendMailRef.current = null;
          }
        }}
        requiredVersion={process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1"}
        currentVersion={desktopAppCurrentVersion}
      />
      <CustomExchangeRateModal
        open={showCustomRateModal}
        onCancel={() => setShowCustomRateModal(false)}
        onSave={(rate) => {
          if (!quotationId) {
            dispatch(
              setQuotationDetails({
                ...quotationDetails,
                conversion_rate: Number(rate),
              }),
            );
            setShowCustomRateModal(false);
          } else {
            dispatch(
              updateQuotationAPI({
                payload: {
                  quote_id: quotationId,
                  conversion_rate: Number(rate),
                },
                onSuccess: () => {
                  dispatch(
                    getQuotationDetailsAPI({
                      quote_id: quotationId,
                      onSuccess: (response: any) => {
                        dispatch(setQuotationDetails(response));
                        setShowCustomRateModal(false);
                        message.success("Exchange rate updated successfully");
                      },
                      onError: () => {
                        setShowCustomRateModal(false);
                      },
                    }),
                  );
                },
                onError: () => {},
              }),
            );
          }
        }}
        currency={quotationDetails?.currency || ""}
        initialRate={customRateInput}
        exchangeDecimal={exchangeDecimal}
      />
      {isMarginDiscountModalOpen && (
        <MarginDiscountModal
          open={isMarginDiscountModalOpen}
          onCancel={() => setIsMarginDiscountModalOpen(false)}
          onApply={handleApplyMarginDiscount}
          initialMargin={
            quotationDetails?.margin ??
            quotationDetails?.customer_snapshot?.margin ??
            0
          }
          initialDiscount={
            quotationDetails?.customer_snapshot?.discount ??
            quotationDetails?.discount ??
            0
          }
          quotationDetails={quotationDetails}
          maxDiscount={(() => {
            if (isDiscountAllow()) {
              return 100;
            }
            return quotationDetails?.customer_id?.discount
              ? quotationDetails?.customer_id?.discount
              : quotationDetails?.customer_snapshot?.discount !== undefined &&
                quotationDetails?.customer_snapshot?.discount !== null
              ? Number(quotationDetails.customer_snapshot.discount)
              : 0;
          })()}
          isLoading={isDiscountChangeLoading}
        />
      )}
      {showLeadTimeModal && (
        <ChangeEstimatedLeadTimeModal
          open={showLeadTimeModal}
          onClose={() => setShowLeadTimeModal(false)}
          quote_id={quotationId || quotationDetails?._id}
          current_lead_time_days={quotationDetails?.estimated_lead_time}
        />
      )}
    </>
  );
}
